// ─────────────────────────────────────────────────────────────────────────────
// Artist CNTRL, React FOUNDATION Booking (full Schedule view) data layer.
//
// The FULL schedule surface (financials + shows + booking tools) seen by the
// owner / Tour Manager / Management / Booking. Reads the schedule blob
// (GET /api/data/schedule) + the saved quotes (GET /api/data/quotes), computes the
// YTD/Lifetime financial tiles (mirrors schedule-wiring.js calcStats), and exposes
// the role gates that (a) decide Booking-vs-Calendar and (b) hide the Lifetime
// financials from Tour Managers (Bible 06.5 / the access spec).
// ─────────────────────────────────────────────────────────────────────────────

import { apiGet, apiPost, apiPut, ApiError, AUTH_WORKER, STATS_WORKER } from './api';
import { authHeaders } from './auth';
import { useCachedData } from './dataCache';
import { fetchConnections, fetchCurrentUser, getCachedUser } from './dashboard';
import { PRO_TEAMS, fetchCrew, teamOf } from './contacts';
import { isPro } from './plan';
import type { ScheduleResponse, User } from './types';

// Re-export the stats worker base for screens that build /serve-file or /photo
// URLs off the same constant the legacy modal used.
export { STATS_WORKER } from './api';

// ── Flyer art resolution (mirrors schedule-wiring.js :1135-1137) ───────────────
//
// A schedule show's flyer can live as: an R2 key (flyerKey → a /serve-file URL on
// the stats worker, preferred, HTTP-cacheable), a direct worker URL (flyerUrl),
// or an inline data: URL (flyer). The legacy precedence is
// flyerKey → flyerUrl → flyer, gated by isImageFile() so a PDF contract uploaded
// to the flyer slot never renders as a (broken) image. Returns '' when there's no
// usable flyer image → the caller keeps the gradient placeholder (Bible 06.4).

/** True when `url`/`name` denote an image (not a PDF). schedule-wiring.js :2362. */
function isImageFile(url: string, name?: string): boolean {
  if (!url || typeof url !== 'string') return false;
  if (/^data:application\/pdf/i.test(url)) return false;
  if (name && /\.pdf$/i.test(name)) return false;
  return true;
}

/** Resolve a show's flyer image src ('' when none/PDF). flyerKey→flyerUrl→flyer. */
export function flyerSrc(ev: RawBookingEvent): string {
  const src =
    (ev.flyerKey ? STATS_WORKER + '/serve-file?key=' + encodeURIComponent(String(ev.flyerKey)) : '') ||
    ev.flyerUrl ||
    ev.flyer ||
    '';
  return src && isImageFile(src, ev.flyerName) ? src : '';
}

// ── Raw schedule event (the money/lineup fields calcStats + the rows read) ──────
//
// NOTE: extended to cover EVERY field the legacy show-edit modal
// (schedule-wiring.js saveFormToEvent) reads + writes, so a faithfully-ported
// React modal round-trips the schedule blob without lossy save payloads.
export interface RawFinancials {
  totalPay?: number;
  guarantee?: number;
  doorSplit?: string | number | boolean;
  /** Ticket face price, drives the mobile DOS auto-calc:
      Total rev = (GA sold + walk ups) × ticketPrice. */
  ticketPrice?: number;
  perPerson?: number;
  hotel?: number;
  flights?: number;
  perDiem?: number;
  memberCount?: number;
  gas?: number;
  rental?: number;
  photoVideo?: number;
  foh?: number;
  agentPercent?: number;
  managerPercent?: number;
  otherExpenses?: ReadonlyArray<{ label?: string; amount?: number }>;
}

/** One screenshot-parsed / manually-entered travel item (flight | hotel). */
export interface TravelItem {
  type?: 'flight' | 'hotel';
  assignedTo?: string;
  cost?: number | string;
  // flight
  airline?: string;
  flightNumber?: string;
  depAirport?: string;
  arrAirport?: string;
  depDate?: string;
  depTime?: string;
  arrDate?: string;
  arrTime?: string;
  seat?: string;
  // hotel
  hotel?: string;
  address?: string;
  checkIn?: string;
  checkOut?: string;
  roomType?: string;
  // shared
  confirmation?: string;
  passengerName?: string;
  [k: string]: unknown;
}

/** One VIP-list record (name + optional email). */
export interface VipRecord { name: string; email: string }

/** A member snapshot attached to a show (drives the push + SMS hold flow). */
export interface MemberContact {
  id?: string | number | null;
  firstName?: string;
  lastName?: string;
  phone?: string;
  email?: string;
  avatarUrl?: string;
  userId?: string | number;
}

/** One settlement-time expense row ({ id, category, amount, notes }). */
export interface SettlementExpense {
  id?: string;
  category?: string;
  amount?: number;        // actual
  budget?: number;        // budgeted (Prism settlements carry budget vs actual)
  group?: 'fixed' | 'variable';
  notes?: string;
}
/** One per-artist payout row (flat $ or % of headline take). */
export interface SettlementPayout {
  id?: string;
  artist_name?: string;
  amount?: number;
  kind?: 'flat' | 'percent';
}
/** The two-tier door / VIP / comps model (presale + walk-up priced separately). */
export interface SettlementDoor {
  presale_sold?: number;
  presale_price?: number;
  walkup_sold?: number;
  walkup_price?: number;
  vip_sold?: number;
  vip_price?: number;
  comps_in?: number;
  // Legacy fields (kept for back-compat reads on other surfaces).
  ga_sold?: number;
  ga_price?: number;
  walkup_pct?: number;
  /** Mobile day-of: a direct door-gross dollar entry (band types the total). */
  total_rev?: number;
}
export interface SettlementSplits {
  guarantee?: number;
  deposit?: number;
  door_split_pct?: number;
  door_split_after?: number;
  tax_withholding_pct?: number;   // % withheld from artist earnings (e.g. Prism 7%)
  settled_split_base?: number;    // pre-computed pool the split % is taken on (net revenue to split)
  bonuses?: ReadonlyArray<{ label?: string; amount?: number }>;
  payouts?: ReadonlyArray<SettlementPayout>;
  [k: string]: unknown;
}
export interface SettlementMerchCut {
  enabled?: boolean;
  /** Soft-goods (apparel) cut % — the venue's take on 85/15-type merch. This is
      the rate cut_owed is computed from (Ryan 2026-07-21: apparel dominates). */
  percentage?: number;
  /** Recorded (CD/vinyl) cut % — the 90/10-type rate. Stored for reference; the
      cut_owed calc uses `percentage` since POS gives one blended sales total. */
  percentage_recorded?: number;
  sales_amount?: number;
  sales_source?: string;
  cut_owed?: number;
  last_pulled_at?: string | null;
}
/** Settlement sub-blob stored on the event row (the full worksheet state). */
/** One money-in event against a show (deposit on signing, post-show balance, a
 *  partial wire, etc.). The deposit split is the "on signing" payment; everything
 *  after lives here so the Outstanding card can track money still owed. */
export interface SettlementPayment {
  id: string;
  /** ISO date (YYYY-MM-DD) the payment was received. */
  date: string;
  amount: number;
  note?: string;
}
export interface SettlementBlob {
  status?: 'pending' | 'in_progress' | 'settled';
  door?: SettlementDoor;
  expenses?: ReadonlyArray<SettlementExpense>;
  /** Venue/show expenses from a settlement (pre-split — they set the door share,
      not the band's take). Read-only on the artist side; kept apart from the
      band's own editable expenses. (Ryan 2026-07-22) */
  show_expenses?: ReadonlyArray<SettlementExpense>;
  splits?: SettlementSplits;
  merch_cut?: SettlementMerchCut;
  /** Money received after the deposit (balance, partials). received = deposit + Σ. */
  payments_received?: ReadonlyArray<SettlementPayment>;
  net_to_artist?: number;
  /** Prism's own stated totals, kept verbatim for reconciliation + the venue
      side (gross, net gross, net-to-split, promoter profit/loss, payout). */
  reference?: {
    source?: string; gross?: number; net_gross?: number; net_revenue_to_split?: number;
    profit_loss?: number; attendance?: number; artist_payout?: number;
    expenses_total?: number; venue_net?: number;
  };
  settled_at?: string | null;
  updated_at?: string | null;
  [k: string]: unknown;
}
/** setlist 2026-07-12 (Ryan), one entry in a show's ordered setlist.
    `source` records where it came from ('spotify' | 'release' | 'custom');
    purely informational, never gates anything. */
export interface SetlistItem {
  id?: string;
  title: string;
  note?: string;
  source?: string;
}

export interface RawBookingEvent {
  id?: string | number;
  /** created-by 2026-07-17: the creating user's id, the schedule delete gate
   *  uses it to tell "mine" from "legacy" (see saveShow stamp). */
  createdById?: string;
  type?: string;
  status?: string;
  confirmed?: boolean;
  date?: string;
  venue?: string;
  city?: string;
  name?: string;
  support?: string | ReadonlyArray<string>;
  /** Comma-joined act list the schedule form stores ("Whitsett, Bleach, …"). */
  otherActs?: string;
  /** Real lineup is an array of objects ({name, role, set times…}); legacy could be strings. */
  lineup?: string | ReadonlyArray<LineupAct>;
  venueAddress?: string;
  capacity?: number;
  /** Age restriction label from the contract ("All ages" | "16+" | "18+" | "21+"). */
  ageRestriction?: string;
  /** Ticket tiers from the contract (GA / Balcony / …): name + cap + face price.
      Σ cap = house capacity; Σ cap×price = gross potential (Ryan 2026-07-21). */
  ticketTiers?: ReadonlyArray<{ name?: string; cap?: number; price?: number; sold?: number }>;
  /** Co-headline VIP: when true, VIP gross is pulled OUT of the door-split pool
      and this artist is credited 50% of VIP gross directly (venue collects and
      splits to both co-headliners). Only meaningful on co-headline bills.
      (Ryan 2026-07-22) */
  coVipSplit?: boolean;
  financials?: RawFinancials;
  merch?: {
    total?: number; itemCount?: number; source?: string;
    // Written by syncMerchToShows (POS sync); optional so manual/legacy rows still fit.
    orders?: number; allOrders?: number; sources?: unknown; provider?: 'shopify' | 'square';
    syncedAt?: string; manual?: boolean; window?: unknown;
  };
  tickets?: { presale?: number; walkUp?: number };
  // ── extra fields the View details modal renders ──
  tourName?: string;
  promoter?: string;
  notes?: string;
  ticketLink?: string;
  memberContacts?: ReadonlyArray<MemberContact>;
  responses?: Record<string, string>;
  dayOfShow?: {
    doors?: string;
    loadIn?: string | Record<string, string> | null;
    loadOut?: string;
    loadInProd?: string;
    loadOutNotes?: string;
    production?: string;
    /** Support changeover (min). Default 20. The legacy single change-over field. */
    changeOver?: string | number;
    /** Headliner changeover (min). Default 20; mirrors `changeOver` until edited. NEW (Run of Show). */
    changeOverHeadliner?: string | number;
    /** Show end clock time (15-min granular). Default "11:00 pm". */
    showEnd?: string;
    /** Headliner load-in clock time (15-min granular). Default "3:00 pm". NEW (Run of Show). */
    headlinerLoadIn?: string;
    vipTime?: string;
    vipSource?: string;
    vipList?: ReadonlyArray<VipRecord>;
    [k: string]: unknown;
  };
  contract?: string;
  contractUrl?: string;
  contractKey?: string;
  contractName?: string;
  /** Free-text deal terms (added to Finance per the show-editor spec). */
  terms?: string;
  /** setlist 2026-07-12: the show's ordered setlist (editor section 06). */
  setlist?: ReadonlyArray<SetlistItem>;
  flyer?: string;
  flyerUrl?: string;
  flyerKey?: string;
  flyerName?: string;
  bitUrl?: string;
  // ── show-edit-modal fields (legacy saveFormToEvent) ──
  showCategory?: string;
  /** Off/travel day (tour builder + "+ Off / travel day"), recessed everywhere,
   *  never a show row, never pushes to BIT, never carries money. */
  isOffDay?: boolean;
  // ── off/travel-day logistics (CD spec 2026-07-01) ──
  /** Route day: origin city ("Nashville, TN"). Single-city days use `city` only. */
  fromCity?: string;
  /** Route day: destination city ("Denver, CO"). */
  toCity?: string;
  /** Departure / rolls time, free text ("9:30 AM"). */
  departTime?: string;
  /** Bus call location ("Hotel lobby"). */
  busCall?: string;
  /** Destination hotel ("The Curtis, Denver"). */
  hotel?: string;
  /** Booking pipeline stage. 'offer' = a pre-hold (the Offer kanban column);
   *  promoting an offer to a hold just clears this. Absent = a normal show/hold. */
  dealStage?: 'offer' | 'hold' | 'confirmed';
  /** Offer "good through" date (the buyer's deadline). Offers only. */
  offerGoodThrough?: string;
  travel?: ReadonlyArray<TravelItem>;
  settlement?: SettlementBlob;
  // Bandsintown venue / festival pick snapshot
  bitVenueId?: string | number | null;
  bitFestivalId?: string | number | null;
  bitFestivalName?: string | null;
  bitEditionId?: string | null;
  bitEditionYear?: string | null;
  bitVenuePick?: Record<string, unknown> | null;
  // Shared-show (Friends bridge) collab markers
  _sharedFromBand?: string;
  _sharedHostBandId?: string;
  _sharedDateId?: string;
  // Bandsintown push state (set by the show-details modal push flow)
  bitPushed?: boolean;
  bitPushedAt?: string;
  bitEventId?: string;
  // Show-hold invite state (stamped by inviteMembersToHold)
  invitees?: ReadonlyArray<{ name: string; userId: string; email: string }>;
  invitedAt?: string;
  // Per-show coordinates (BIT pick), used by the legacy "find local support" CTA
  latitude?: number | null;
  longitude?: number | null;
  // Promoter / show-category extras already present below; media link row
  mediaLink?: string;
  fromOpportunity?: boolean;
  title?: string;
}
export interface LineupAct {
  name?: string;
  role?: string;
  bit_id?: string | number | null;
  bit_img?: string | null;
  startTime?: string;
  setLength?: string;
  soundcheckStart?: string;
  soundcheckLength?: string;
  loadIn?: string;
  loadOut?: string;
  /** Editing-session-only flag: true ⇒ this slot was auto-filled by the
   *  cascade and may be overwritten. NEVER persisted (stripped on save). */
  _autoStart?: boolean;
}

// ── Display models ──────────────────────────────────────────────────────────────

/** A computed financial tile pair (YTD + Lifetime). */
export interface BookingStats {
  readonly count: number;
  readonly guaranteesYtd: number;
  readonly guaranteesLife: number;
  readonly merchYtd: number;
  readonly merchLife: number;
  readonly ticketsYtd: number;
  readonly ticketsLife: number;
  readonly netYtd: number;
  readonly netLife: number;
}

/** One show row (Confirmed / Holds / Past). */
export interface BookingShow {
  readonly id: string;
  readonly date: string;
  readonly dow: string;
  readonly day: string;
  /** Lineup line ("Whitsett · Keep Warm · …"). */
  readonly bill: string;
  /** "Venue · City". */
  readonly venue: string;
  readonly guarantee: number | null;
  readonly confirmed: boolean;
  readonly isPast: boolean;
  /** The full underlying schedule event (the View details modal reads it). */
  readonly raw: RawBookingEvent;
}

/** A saved guarantee quote (GET /api/data/quotes). */
export interface BookingQuote {
  readonly id: string;
  readonly venue: string;
  readonly meta: string;
}

const DOW = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];
const pad = (n: number): string => String(n).padStart(2, '0');

// ── Stats computation (mirrors schedule-wiring.js calcStats :850-919) ───────────
//
// Per confirmed show: guarantees += financials.totalPay; merch += merch.total;
// net += totalPay + merch − expenses − agent% − manager% − other; tickets +=
// presale + walkUp. YTD = confirmed shows dated this year up to now; Lifetime =
// all confirmed PAST shows. (The live-settlement net override is omitted in this
// foundation pass, uses the booked totalPay as `take`.)
function accumulate(
  acc: { g: number; m: number; net: number; t: number },
  ev: RawBookingEvent,
): void {
  const f = ev.financials || {};
  const total = Number(f.totalPay ?? f.guarantee) || 0;
  const expenses =
    (Number(f.hotel) || 0) +
    (Number(f.flights) || 0) +
    (Number(f.perDiem) || 0) * (Number(f.memberCount) || 1) +
    (Number(f.gas) || 0) +
    (Number(f.rental) || 0) +
    (Number(f.photoVideo) || 0) +
    (Number(f.foh) || 0);
  const agent = (total * (Number(f.agentPercent) || 0)) / 100;
  const manager = (total * (Number(f.managerPercent) || 0)) / 100;
  const other = (f.otherExpenses || []).reduce((s, x) => s + (Number(x?.amount) || 0), 0);
  const merchAmt = (ev.merch && Number(ev.merch.total)) || 0;
  acc.g += total;
  acc.m += merchAmt;
  acc.net += total + merchAmt - expenses - agent - manager - other;
  acc.t += (Number(ev.tickets?.presale) || 0) + (Number(ev.tickets?.walkUp) || 0);
}

function calcStats(events: ReadonlyArray<RawBookingEvent>): BookingStats {
  const now = new Date();
  const year = now.getFullYear();
  const ytd = { g: 0, m: 0, net: 0, t: 0 };
  const life = { g: 0, m: 0, net: 0, t: 0 };
  let count = 0;
  for (const ev of events) {
    if (ev.type !== 'show' || !ev.confirmed || !ev.date) continue;
    const d = new Date(ev.date + 'T12:00:00');
    if (isNaN(d.getTime())) continue;
    // "If the show is confirmed it counts" (Ryan, 2026-06), a confirmed show's
    // guarantee counts whether it's already happened or upcoming. YTD = confirmed
    // shows dated this calendar year (past OR future); Lifetime = all confirmed.
    // Matches the roster KPI strip, which already counts confirmed regardless of
    // date. (Merch/tickets on a future show are still $0 until it sells, so only
    // guarantees/P&L move forward.)
    if (d.getFullYear() === year) {
      accumulate(ytd, ev);
      count += 1;
    }
    accumulate(life, ev);
  }
  return {
    count,
    guaranteesYtd: ytd.g,
    guaranteesLife: life.g,
    merchYtd: ytd.m,
    merchLife: life.m,
    ticketsYtd: ytd.t,
    ticketsLife: life.t,
    netYtd: ytd.net,
    netLife: life.net,
  };
}

// ── Show rows ────────────────────────────────────────────────────────────────
function billOf(ev: RawBookingEvent): string {
  // The act list (who's on the bill). Real events store it as `otherActs`
  // (comma string) or `lineup` (array of {name,role}); legacy used `support`.
  if (typeof ev.otherActs === 'string' && ev.otherActs.trim()) return ev.otherActs.trim();
  const lu = ev.lineup;
  if (Array.isArray(lu) && lu.length) {
    const names = lu.map((x) => (typeof x === 'string' ? x : x?.name || '')).filter(Boolean);
    if (names.length) return names.join(' · ');
  }
  const s = ev.support;
  if (typeof s === 'string' && s.trim()) return s.trim();
  if (Array.isArray(s) && s.length) return s.map(String).filter(Boolean).join(' · ');
  return ev.name || ev.venue || 'Show';
}

function toShow(ev: RawBookingEvent): BookingShow {
  const d = new Date(String(ev.date) + 'T12:00:00');
  const venue = [ev.venue || ev.name, ev.city].filter(Boolean).join(' · ');
  const g = Number(ev.financials?.totalPay ?? ev.financials?.guarantee);
  return {
    id: String(ev.id ?? ev.date ?? ''),
    date: String(ev.date),
    dow: isNaN(d.getTime()) ? '' : DOW[d.getDay()],
    day: isNaN(d.getTime()) ? '' : pad(d.getDate()),
    bill: billOf(ev),
    venue,
    guarantee: g > 0 ? g : null,
    confirmed: ev.confirmed !== false,
    isPast: !isNaN(d.getTime()) && d.getTime() < Date.now(),
    raw: ev,
  };
}

export function newShowId(): string {
  return 'e_' + Date.now() + '_' + Math.random().toString(36).slice(2, 6);
}

/** setlist 2026-07-12, "Copy from last show": the most recent OTHER show
 *  (by date, before `beforeDate` when given) that has a non-empty setlist.
 *  Returns the setlist plus a human label ("Venue · 7/3") or null. Read-only. */
export async function fetchLastSetlist(
  beforeDate?: string, excludeId?: string
): Promise<{ setlist: ReadonlyArray<SetlistItem>; label: string } | null> {
  const res = await apiGet<ScheduleResponse>('auth', '/api/data/schedule').catch(() => null);
  if (!res || typeof res !== 'object') return null;
  const arr = unwrapArr<RawBookingEvent>(res);
  const withSet = arr.filter((e) =>
    e && Array.isArray(e.setlist) && e.setlist.length > 0 &&
    (!excludeId || String(e.id) !== String(excludeId)) &&
    (!beforeDate || String(e.date || '') < beforeDate));
  if (!withSet.length) return null;
  withSet.sort((a, b) => String(b.date || '').localeCompare(String(a.date || '')));
  const src = withSet[0];
  const label = [src.name || src.venue || 'Last show', src.date || ''].filter(Boolean).join(' · ');
  return { setlist: src.setlist as ReadonlyArray<SetlistItem>, label };
}

/** Upsert a show into the schedule blob (read-modify-write, PUT {value}).
 *  audit 2026-07-02: fail closed, if the pre-write GET fails, THROW instead of
 *  proceeding with `[]`, or a transient failure would replace the whole schedule
 *  with this one event. PUT failures are rethrown (no more silent swallow). */
/* hide-for-me 2026-07-17 (Ryan, AV+ seat: "deployed, deleted, still there")
  , the schedule PUT gate converts a non-full-access member's delete into
   responses[<uid>]='pass' on the stored event ("hidden for me"). Legacy
   wiring filtered those rows at render; the React port never did, so a
   "deleted" show kept rendering for the very person who deleted it. Central
   predicate, apply wherever schedule rows become UI. The RMW writers
   (saveShow / deleteShow / tours / merch-sync) must keep the RAW array or a
   full-access save would hard-drop other members' hidden rows. */
export function isHiddenForMe(ev: { responses?: Record<string, string> } | null | undefined): boolean {
  if (!ev || !ev.responses || typeof ev.responses !== 'object') return false;
  const uid = String(getCachedUser()?.id ?? '');
  return !!uid && ev.responses[uid] === 'pass';
}

/* delete-resurrect 2026-07-17 (Ryan: "when I click delete nothing happens…
   when I refresh the show comes back"), saveShow's read-modify-write PUSHES
   the event back when it's missing from the fetched array, so a debounced
   field save landing right after the delete PUT resurrected the show every
   time (edit a field → Delete → the queued 600ms save fires the moment the
   confirm dialog closes). Session tombstones: once an id is deleted, no save
   from this tab may ever write it again. */
const _deletedShowIds = new Set<string>();

export async function saveShow(ev: RawBookingEvent): Promise<void> {
  if (_deletedShowIds.has(String(ev.id ?? ''))) return; // tombstoned, never resurrect
  /* created-by 2026-07-17: NO creation path ever stamped createdById, so the
     server's delete gate treated every event as "legacy", only the literal
     band-primary login could hard-delete anything. Stamp the saver once. */
  const stamped: RawBookingEvent = (ev as { createdById?: unknown }).createdById
    ? ev
    : ({ ...ev, createdById: String(getCachedUser()?.id ?? '') || undefined } as RawBookingEvent);
  const res = await apiGet<ScheduleResponse>('auth', '/api/data/schedule'); // throws on failure
  if (!res || typeof res !== 'object') throw new ApiError('Schedule load returned an unreadable shape, show not saved', 0, '/api/data/schedule');
  const arr = unwrapArr<RawBookingEvent>(res);
  const i = arr.findIndex((x) => String(x.id) === String(stamped.id));
  if (i >= 0) arr[i] = stamped; else arr.push(stamped);
  await apiPut('auth', '/api/data/schedule', { value: arr });
}

/** Batch-append the events a tour builder created (one read-modify-write PUT for
 *  the whole tour), mirroring commitTour in schedule-wiring.js.
 *  audit 2026-07-02: same fail-closed rule as saveShow, a failed GET throws
 *  rather than concatenating onto `[]` and wiping the existing schedule. */
export async function createTourShows(newEvents: ReadonlyArray<RawBookingEvent>): Promise<void> {
  if (!newEvents.length) return;
  const res = await apiGet<ScheduleResponse>('auth', '/api/data/schedule'); // throws on failure
  if (!res || typeof res !== 'object') throw new ApiError('Schedule load returned an unreadable shape, tour not saved', 0, '/api/data/schedule');
  let arr = unwrapArr<RawBookingEvent>(res);
  arr = arr.concat(newEvents as RawBookingEvent[]);
  await apiPut('auth', '/api/data/schedule', { value: arr });
  /* tour-threads 2026-07-27 — the tour builder commits every date in one PUT;
     one ensure call per distinct tour name covers the whole run. */
  const seenTours = new Set<string>();
  for (const ev of newEvents) {
    const tn = String(ev.tourName || '').trim().toLowerCase();
    if (!tn || seenTours.has(tn)) continue;
    seenTours.add(tn);
    ensureShowThread(ev);
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// MERCH SYNC (Sync merch button on Past), faithful port of schedule-wiring.js
// _attachMerchToShows. Picks the provider from the band's connections (Shopify
// wins if both connected, else Square), then for each PAST show computes a
// 6am→next-day-6am window in the VENUE's local timezone and POSTs it to the
// matching POS proxy (/api/proxy/shopify-show | /api/proxy/square-show). The
// returned totals are written to BOTH ev.merch.* (P&L aggregation) and
// ev.settlement.merch_cut.* (settlement view), then the whole schedule blob is
// saved. A user-entered manual merch figure is never clobbered.
// ─────────────────────────────────────────────────────────────────────────────

// US state → IANA timezone (verbatim _attachMerchToShows STATE_TZ).
const PAST_MERCH_STATE_TZ: Record<string, string> = {
  CT: 'America/New_York', DE: 'America/New_York', FL: 'America/New_York', GA: 'America/New_York',
  IN: 'America/New_York', KY: 'America/New_York', MA: 'America/New_York', MD: 'America/New_York',
  ME: 'America/New_York', MI: 'America/New_York', NC: 'America/New_York', NH: 'America/New_York',
  NJ: 'America/New_York', NY: 'America/New_York', OH: 'America/New_York', PA: 'America/New_York',
  RI: 'America/New_York', SC: 'America/New_York', VA: 'America/New_York', VT: 'America/New_York',
  WV: 'America/New_York', DC: 'America/New_York',
  AL: 'America/Chicago', AR: 'America/Chicago', IA: 'America/Chicago', IL: 'America/Chicago',
  KS: 'America/Chicago', LA: 'America/Chicago', MN: 'America/Chicago', MO: 'America/Chicago',
  MS: 'America/Chicago', ND: 'America/Chicago', NE: 'America/Chicago', OK: 'America/Chicago',
  SD: 'America/Chicago', TN: 'America/Chicago', TX: 'America/Chicago', WI: 'America/Chicago',
  CO: 'America/Denver', MT: 'America/Denver', NM: 'America/Denver', UT: 'America/Denver',
  WY: 'America/Denver', ID: 'America/Denver', AZ: 'America/Phoenix',
  CA: 'America/Los_Angeles', NV: 'America/Los_Angeles', OR: 'America/Los_Angeles', WA: 'America/Los_Angeles',
  AK: 'America/Anchorage', HI: 'Pacific/Honolulu',
};
function merchVenueIana(ev: RawBookingEvent): string | null {
  const city = String(ev.city || '');
  const m = city.match(/,\s*([A-Za-z]{2})\b/) || city.match(/\b([A-Za-z]{2})\s*$/);
  if (!m) return null;
  return PAST_MERCH_STATE_TZ[m[1].toUpperCase()] || null;
}
function ianaOffsetForDate(iana: string, yr: number, mo: number, dy: number): string | null {
  try {
    const probe = new Date(Date.UTC(yr, mo - 1, dy, 12));
    const parts = new Intl.DateTimeFormat('en-US', { timeZone: iana, timeZoneName: 'longOffset', hour: '2-digit' }).formatToParts(probe);
    const tz = parts.find((p) => p.type === 'timeZoneName');
    if (!tz) return null;
    const m = String(tz.value).match(/GMT([+-])(\d{1,2})(?::?(\d{2}))?/);
    if (!m) return null;
    const hh = m[2].length < 2 ? '0' + m[2] : m[2];
    return m[1] + hh + ':' + (m[3] || '00');
  } catch { return null; }
}
function browserOffsetForDate(yr: number, mo: number, dy: number): string {
  const off = -new Date(yr, mo - 1, dy, 12, 0, 0).getTimezoneOffset();
  const sign = off >= 0 ? '+' : '-';
  const abs = Math.abs(off);
  const hh = String(Math.floor(abs / 60)).padStart(2, '0');
  const mm = String(abs % 60).padStart(2, '0');
  return sign + hh + ':' + mm;
}
function merchTzOffset(ev: RawBookingEvent, yr: number, mo: number, dy: number): string {
  const iana = merchVenueIana(ev);
  if (iana) { const o = ianaOffsetForDate(iana, yr, mo, dy); if (o) return o; }
  return browserOffsetForDate(yr, mo, dy);
}

interface PosShowResponse {
  total?: number | string; itemCount?: number | string; orderCount?: number | string;
  allOrderCount?: number | string; sources?: unknown; window?: unknown; error?: string; code?: string;
}
export interface MerchSyncResult {
  provider: 'shopify' | 'square' | null;
  /** Past shows that successfully queried the POS (whether or not they had sales). */
  updated: number;
  /** Shows that actually came back with merch dollars (> $0). */
  withMerch: number;
  /** Sum of merch dollars attached across all shows. */
  totalAmount: number;
  /** Shows that still failed after one retry (transient, rate limit/timeout). */
  failed: number;
  /** True when the POS returned SCOPE_UPGRADE for an out-of-window (>60d) show,
   *  the connection must be re-authed to read older orders. Surfaced to the user
   *  so an all-2024 roster doesn't just silently report "0 synced". */
  scopeUpgrade: boolean;
}

// past-show-actions 2026-07-05: the provider pick + per-show window/write below
// were extracted VERBATIM from syncMerchToShows so the mobile single-show sync
// (syncMerchForShow) runs the exact same logic for one event.

/** Pick the POS provider off the band's connections (Shopify wins over Square). */
async function pickPosProvider(): Promise<{ provider: 'shopify' | 'square' | null; path: string; sourceLabel: string }> {
  const conn = await fetchConnections().catch(() => ({} as Record<string, { [k: string]: unknown }>));
  const sh = conn.shopify;
  const sq = conn.square;
  const hasShopify = !!(sh && (sh.store || sh.shop || sh._connectedRedacted));
  const hasSquare = !!(sq && (sq.merchantId || (sq as { merchant_id?: unknown }).merchant_id || sq._connectedRedacted));
  if (hasShopify) return { provider: 'shopify', path: '/api/proxy/shopify-show', sourceLabel: 'shopify POS' };
  if (hasSquare) return { provider: 'square', path: '/api/proxy/square-show', sourceLabel: 'square POS' };
  return { provider: null, path: '', sourceLabel: '' };
}

/** 6am→next-day-6am sales window in the venue's local tz (null = malformed date). */
function posShowWindow(ev: RawBookingEvent): { startISO: string; endISO: string } | null {
  const date = String(ev.date || '').slice(0, 10);
  const [yr, mo, dy] = date.split('-').map((n) => parseInt(n, 10));
  if (!yr || !mo || !dy) return null;
  const end = new Date(yr, mo - 1, dy + 1);
  const endY = end.getFullYear();
  const endM = String(end.getMonth() + 1).padStart(2, '0');
  const endD = String(end.getDate()).padStart(2, '0');
  const startISO = date + 'T06:00:00' + merchTzOffset(ev, yr, mo, dy);
  const endISO = endY + '-' + endM + '-' + endD + 'T06:00:00' + merchTzOffset(ev, endY, Number(endM), Number(endD));
  return { startISO, endISO };
}

/** Write a POS response onto ev.merch (P&L) + ev.settlement.merch_cut (settlement
 *  view), a user-entered manual merch figure is never clobbered. Returns the
 *  pulled dollar total. */
function writePosMerch(
  ev: RawBookingEvent,
  data: PosShowResponse,
  provider: 'shopify' | 'square',
  sourceLabel: string,
  startISO: string,
  endISO: string,
): number {
  const totalNum = parseFloat(String(data.total || 0)) || 0;
  ev.merch = {
    total: totalNum,
    itemCount: parseInt(String(data.itemCount || 0), 10) || 0,
    orders: parseInt(String(data.orderCount || 0), 10) || 0,
    allOrders: parseInt(String(data.allOrderCount || 0), 10) || 0,
    sources: data.sources || null,
    source: sourceLabel,
    provider,
    syncedAt: new Date().toISOString(),
    manual: false,
    window: data.window || { start: startISO, end: endISO },
  } as RawBookingEvent['merch'];
  const st = (ev.settlement || {}) as { merch_cut?: { sales_amount?: number; sales_source?: string; last_pulled_at?: string } };
  if (!st.merch_cut) st.merch_cut = { sales_amount: 0, sales_source: 'manual' };
  const existing = Number(st.merch_cut.sales_amount) || 0;
  const wasManual = st.merch_cut.sales_source === 'manual' && existing > 0;
  if (!wasManual) {
    st.merch_cut.sales_amount = totalNum;
    st.merch_cut.sales_source = provider;
    st.merch_cut.last_pulled_at = new Date().toISOString();
  }
  (ev as { settlement?: unknown }).settlement = st;
  return totalNum;
}

/** Sync POS merch onto past shows. Returns the provider used + #shows updated;
 *  provider null means neither Shopify nor Square is connected. */
export async function syncMerchToShows(): Promise<MerchSyncResult> {
  const picked = await pickPosProvider();
  const provider = picked.provider;
  const path = picked.path;
  const sourceLabel = picked.sourceLabel;
  if (!provider) return { provider: null, updated: 0, withMerch: 0, totalAmount: 0, failed: 0, scopeUpgrade: false };

  const res = await apiGet<ScheduleResponse>('auth', '/api/data/schedule').catch(() => null);
  const arr = unwrapArr<RawBookingEvent>(res);
  if (!Array.isArray(arr) || arr.length === 0) return { provider, updated: 0, withMerch: 0, totalAmount: 0, failed: 0, scopeUpgrade: false };
  let scopeUpgrade = false;

  const todayIso = new Date().toISOString().slice(0, 10);
  const past = arr.filter((ev) => {
    const t = String(ev.type || 'show').toLowerCase();
    if (t !== 'show' && t !== '') return false;
    const d = String(ev.date || '').slice(0, 10);
    return !!d && d <= todayIso;
  });

  let updated = 0;
  let withMerch = 0;
  let totalAmount = 0;

  // Process ONE show. Returns true when the POS responded (or the show was
  // unprocessable / scope-blocked, i.e. nothing to retry); false on a transient
  // failure (network/timeout/rate-limit/5xx) that's worth one retry. Bursting
  // every show in parallel made Shopify drop some under rate-limiting, which is
  // why an earlier run reported fewer shows than exist, so we batch + retry.
  const processOne = async (ev: RawBookingEvent): Promise<boolean> => {
    const win = posShowWindow(ev);
    if (!win) return true; // malformed date, skip, not retryable
    const { startISO, endISO } = win;
    let data: PosShowResponse | null = null;
    try {
      data = await apiPost<PosShowResponse>('auth', path, { start: startISO, end: endISO });
    } catch (e) {
      // 428 + {code:'SCOPE_UPGRADE'} = orders older than 60d without read_all_orders.
      // Not retryable, flag for the reconnect prompt.
      if (e instanceof ApiError && (e.status === 428 || (e.body || '').includes('SCOPE_UPGRADE'))) { scopeUpgrade = true; return true; }
      return false; // transient, retry
    }
    if (data && data.code === 'SCOPE_UPGRADE') { scopeUpgrade = true; return true; }
    if (!data || data.error) return false; // treat as transient, retry once
    const totalNum = writePosMerch(ev, data, provider, sourceLabel, startISO, endISO);
    updated += 1;
    if (totalNum > 0) { withMerch += 1; totalAmount += totalNum; }
    return true;
  };

  // Run in small batches with a gap between them to stay under Shopify's ~2 req/s
  // REST limit (each proxy call hits Shopify once); collect transient failures
  // (429/timeout) and retry them once after a longer cool-off.
  const sleep = (ms: number): Promise<void> => new Promise((r) => setTimeout(r, ms));
  /* square-sync-429 2026-07-18: the 2-wide/600ms pacing was tuned for
     Shopify's ~2 req/s. Square's limiter is tighter and its proxy calls are
     heavier, a 63-show catalog walked at Shopify pace got walled with 429s
     and wrote $0s. Square runs single-file with a wider gap (~1 call/s;
     locations are server-cached now); Shopify keeps its proven pacing. */
  const CHUNK = provider === 'square' ? 1 : 2;
  const GAP_MS = provider === 'square' ? 1000 : 600;
  const RETRY_COOLOFF_MS = provider === 'square' ? 5000 : 1500;
  const runBatched = async (list: RawBookingEvent[]): Promise<RawBookingEvent[]> => {
    const fails: RawBookingEvent[] = [];
    for (let i = 0; i < list.length; i += CHUNK) {
      const batch = list.slice(i, i + CHUNK);
      const oks = await Promise.all(batch.map(processOne));
      batch.forEach((ev, j) => { if (!oks[j]) fails.push(ev); });
      if (i + CHUNK < list.length) await sleep(GAP_MS);
    }
    return fails;
  };
  const firstFails = await runBatched(past);
  let stillFailed: RawBookingEvent[] = [];
  if (firstFails.length) { await sleep(RETRY_COOLOFF_MS); stillFailed = await runBatched(firstFails); }
  const failed = stillFailed.length;

  try { await apiPut('auth', '/api/data/schedule', { value: arr }); } catch { /* swallow, surfaced as 0 by caller refetch */ }
  return { provider, updated, withMerch, totalAmount, failed, scopeUpgrade };
}

// past-show-actions 2026-07-05: single-show merch sync for the mobile past-show
// sheet. Same provider pick, same venue-local 6am→6am window, same ev.merch +
// settlement.merch_cut write as syncMerchToShows, but for ONE show, with a
// fail-closed schedule read (a failed GET throws instead of wiping the blob,
// same rule as saveShow).
export interface SingleMerchSyncResult {
  provider: 'shopify' | 'square' | null;
  ok: boolean;
  /** Merch dollars pulled for this show ($0 = POS answered, no sales in window). */
  amount: number;
  scopeUpgrade: boolean;
  error?: string;
}

/** Sync POS merch onto ONE show (mobile past-show sheet). */
export async function syncMerchForShow(showId: string): Promise<SingleMerchSyncResult> {
  const { provider, path, sourceLabel } = await pickPosProvider();
  if (!provider) return { provider: null, ok: false, amount: 0, scopeUpgrade: false };

  const res = await apiGet<ScheduleResponse>('auth', '/api/data/schedule'); // throws on failure (fail closed)
  if (!res || typeof res !== 'object') return { provider, ok: false, amount: 0, scopeUpgrade: false, error: 'Schedule load failed' };
  const arr = unwrapArr<RawBookingEvent>(res);
  const ev = arr.find((e) => String(e.id) === String(showId));
  if (!ev) return { provider, ok: false, amount: 0, scopeUpgrade: false, error: 'Show not found' };
  const win = posShowWindow(ev);
  if (!win) return { provider, ok: false, amount: 0, scopeUpgrade: false, error: 'Show has no date' };

  let data: PosShowResponse | null = null;
  try {
    data = await apiPost<PosShowResponse>('auth', path, { start: win.startISO, end: win.endISO });
  } catch (e) {
    // 428 + {code:'SCOPE_UPGRADE'} = orders older than 60d without read_all_orders.
    if (e instanceof ApiError && (e.status === 428 || (e.body || '').includes('SCOPE_UPGRADE'))) {
      return { provider, ok: false, amount: 0, scopeUpgrade: true };
    }
    return { provider, ok: false, amount: 0, scopeUpgrade: false, error: 'POS request failed' };
  }
  if (data && data.code === 'SCOPE_UPGRADE') return { provider, ok: false, amount: 0, scopeUpgrade: true };
  if (!data || data.error) return { provider, ok: false, amount: 0, scopeUpgrade: false, error: (data && data.error) || 'POS request failed' };

  const amount = writePosMerch(ev, data, provider, sourceLabel, win.startISO, win.endISO);
  await apiPut('auth', '/api/data/schedule', { value: arr }); // throws on failure
  return { provider, ok: true, amount, scopeUpgrade: false };
}

/** Delete a show, read-modify-write the schedule blob (PUT {value: events},
 *  mirrors schedule-wiring.js _doPutSchedule). Returns false if not found.
 *  ALSO scrubs the opportunities mirror: an opp-sourced show carries an
 *  `opportunityId`, and if the source opp survives in /api/data/opportunities it
 *  re-materializes on the next Calendar merge (the dual-store ghost-row bug). */
/* delete-honesty 2026-07-18 (Ryan: three people "deleted" a show that four
   surfaces kept showing), mirrors the worker gate's av-delete v2 policy:
   band_member_av has the EXACT same authority as the primary artist seat.
   Lesser seats' deletes are downgraded server-side to hide-for-me; the UI
   must SAY so instead of letting them believe the show died band-wide. */
const FULL_ACCESS_ROLES = new Set(['artist', 'band_member_av', 'admin', 'jedi', 'superadmin']);
function isFullAccessSeat(): boolean {
  const u = getCachedUser() as { role?: string; membership_role?: string; is_band_primary?: boolean } | null;
  if (!u) return false;
  if (u.is_band_primary) return true;
  return FULL_ACCESS_ROLES.has(String(u.role || '').toLowerCase()) ||
         FULL_ACCESS_ROLES.has(String(u.membership_role || '').toLowerCase());
}

export async function deleteShow(id: string): Promise<boolean> {
  /* Tombstone FIRST, any in-flight or queued saveShow for this id becomes a
     no-op even while the delete's own round-trip is still running. */
  _deletedShowIds.add(String(id));
  const res = await apiGet<ScheduleResponse>('auth', '/api/data/schedule').catch(() => null);
  let arr = unwrapArr<RawBookingEvent>(res);
  if (!Array.isArray(arr)) arr = [];
  const doomed = arr.find((e) => String(e.id) === String(id));
  const next = arr.filter((e) => String(e.id) !== String(id));
  if (next.length === arr.length) return false; // already gone server-side, keep the tombstone
  try { await apiPut('auth', '/api/data/schedule', { value: next }); }
  catch {
    _deletedShowIds.delete(String(id)); // delete failed, the show still exists; allow saves again
    return false;
  }
  if (!isFullAccessSeat()) {
    /* delete-honesty 2026-07-18: the worker downgraded this to hide-for-me. */
    window.alert('Removed from YOUR view only. Your seat can\u2019t delete shows for the whole band \u2014 the show still exists for everyone else. Ask an artist or AV member to delete it band-wide.');
  }

  // Best-effort opportunities scrub, match the source opp by the show id and the
  // show's opportunityId, against opp.id / opp._originalId.
  const oppKeys = new Set<string>([String(id)]);
  const oppLink = doomed && (doomed as { opportunityId?: string | number }).opportunityId;
  if (oppLink != null && oppLink !== '') oppKeys.add(String(oppLink));
  try {
    const og = await apiGet<ScheduleResponse>('auth', '/api/data/opportunities').catch(() => null);
    let opps = unwrapArr<RawBookingEvent>(og);
    if (!Array.isArray(opps)) opps = [];
    const nextOpps = opps.filter((o) => {
      const oid = String((o as { id?: unknown }).id ?? '');
      const oOrig = String((o as { _originalId?: unknown })._originalId ?? '');
      return !(oppKeys.has(oid) || (oOrig !== '' && oppKeys.has(oOrig)));
    });
    if (nextOpps.length !== opps.length) await apiPut('auth', '/api/data/opportunities', { value: nextOpps });
  } catch { /* mirror scrub is best-effort; schedule delete already succeeded */ }
  return true;
}

// ════════════════════════════════════════════════════════════════════════════
// SHOW-EDIT MODAL DATA LAYER, faithful port of schedule-wiring.js openShowForm /
// saveFormToEvent / confirmAndAutoApplyMembers + the modal's contacts, commissions,
// Bandsintown autocomplete, travel-screenshot parse, and notify calls.
// ════════════════════════════════════════════════════════════════════════════

/** cntrl-api worker, the travel-screenshot vision parser lives here. */
const API_WORKER = 'https://cntrl-api.whitsettband.workers.dev';

// ── Commissions defaults (agent / manager %) ──────────────────────────────────
export interface Commissions { agentPercent: number; managerPercent: number }

/** GET /api/data/commissions → the saved default agent/manager %. */
export async function fetchCommissions(): Promise<Commissions> {
  const res = await apiGet<ScheduleResponse>('auth', '/api/data/commissions').catch(() => null);
  let val: unknown = null;
  if (res && typeof res === 'object') {
    const obj = res as ScheduleResponse & { value?: unknown };
    const data = obj.data as { value?: unknown } | undefined;
    val = (data && data.value) ?? data ?? obj.value ?? null;
  }
  const c = (val && typeof val === 'object' ? val : {}) as { agentPercent?: number; managerPercent?: number };
  return { agentPercent: Number(c.agentPercent) || 0, managerPercent: Number(c.managerPercent) || 0 };
}

/** PUT /api/data/commissions (mirror saveCommissions, { value: commissions }). */
export async function saveCommissions(c: Commissions): Promise<void> {
  try { await apiPut('auth', '/api/data/commissions', { value: c }); } catch { /* swallow */ }
}

// ── Contacts for the member picker ────────────────────────────────────────────
/** A contact row the member-picker shows (legacy addressBook entry). */
export interface PickerContact {
  id?: string | number | null;
  firstName?: string;
  lastName?: string;
  phone?: string;
  email?: string;
  role?: string;
  avatarUrl?: string;
  avatar_url?: string;
  userId?: string | number;
  teamUserId?: string | number;
}

/** GET /api/data/contacts → the band address book for the member picker. */
export async function fetchAddressBook(): Promise<PickerContact[]> {
  const res = await apiGet<ScheduleResponse>('auth', '/api/data/contacts').catch(() => null);
  return unwrapArr<PickerContact>(res);
}

// ── Bandsintown autocomplete (venue / festival / artist) ──────────────────────
export interface BitHit {
  id?: string | number;
  name?: string;
  imageUrl?: string;
  mediaId?: string;
  address?: string;
  city?: string;
  region?: string;
  country?: string;
  latitude?: number | null;
  longitude?: number | null;
  verified?: boolean;
  trackerCount?: number;
  place_id?: string;
  bit_venue_id?: string | number | null;
  sub?: string;
  _locLabel?: string;
  [k: string]: unknown;
}

/** GET /api/bandsintown/<kind>-autocomplete (mirrors attachAutocomplete fetchResults). */
export async function bitAutocomplete(kind: 'venue' | 'festival' | 'artist', query: string): Promise<BitHit[]> {
  const url = AUTH_WORKER + '/api/bandsintown/' + kind + '-autocomplete?query=' + encodeURIComponent(query);
  try {
    const r = await fetch(url, { headers: authHeaders('GET'), cache: 'no-store' });
    if (!r.ok) return [];
    const res = await r.json() as { venues?: BitHit[]; festivals?: BitHit[]; artists?: BitHit[] };
    let list: BitHit[] = res.venues || res.festivals || res.artists || [];
    if (kind === 'festival') list = list.map((it) => (it && it.verified == null ? { ...it, verified: true } : it));
    // Drop Google-Places-only rows (no BIT id ⇒ can't push to BIT) for venues.
    return list.filter((it) => !(it.place_id && !it.bit_venue_id));
  } catch { return []; }
}

export interface FestivalEdition { id: string; year: string; label: string }

/** GET /api/bandsintown/festival-editions (mirrors loadFestivalEditions). */
export async function fetchFestivalEditions(festivalId: string | number): Promise<FestivalEdition[]> {
  const url = AUTH_WORKER + '/api/bandsintown/festival-editions?festival_id=' + encodeURIComponent(String(festivalId));
  try {
    const r = await fetch(url, { headers: authHeaders('GET'), cache: 'no-store' });
    if (!r.ok) return [];
    const res = await r.json() as { editions?: Array<Record<string, unknown>> };
    const rows = Array.isArray(res.editions) ? res.editions : [];
    return rows.map((e) => {
      const id = String(e.id ?? e.edition_id ?? '');
      const year = String(e.year ?? e.startYear ?? e.start_date ?? '').slice(0, 4);
      return { id, year, label: year || id };
    }).filter((e) => e.id);
  } catch { return []; }
}

// ── Travel screenshot parse (Gemini Vision via cntrl-api) ──────────────────────
export interface TravelLineupHint { name: string; members: string[] }

/** POST /parse-travel, returns structured travel items (mirrors parseTravelScreenshot). */
export async function parseTravelScreenshot(imageBase64: string, lineup: TravelLineupHint[]): Promise<TravelItem[]> {
  try {
    const r = await fetch(API_WORKER + '/parse-travel', {
      method: 'POST',
      headers: authHeaders('POST'),
      body: JSON.stringify({ image_base64: imageBase64, lineup }),
    });
    const j = await r.json() as { items?: TravelItem[]; flights?: TravelItem[]; hotels?: TravelItem[] };
    if (!r.ok) return [];
    const flights = (j.flights || []).map((t) => ({ ...t, type: 'flight' as const }));
    const hotels = (j.hotels || []).map((t) => ({ ...t, type: 'hotel' as const }));
    if (Array.isArray(j.items) && j.items.length) return j.items;
    return [...flights, ...hotels];
  } catch { return []; }
}

// ── Run-of-Show screenshot parse (Gemini Vision via cntrl-api) ─────────────────
// Fallback for entering a run of show when the venue isn't connected via Venue
// CNTRL. Mirrors parseTravelScreenshot exactly (same API_WORKER base, same
// authHeaders, same try/catch envelope). Times come back as human clock strings;
// durations come back as minute numbers, the modal snaps them to the valid
// option sets.

/** One per-band run-of-show row returned by /parse-ros. */
export interface RosRow {
  band?: string;
  loadIn?: string | null;
  soundcheck?: string | null;
  soundcheckMin?: number | null;
  setTime?: string | null;
  setLength?: number | null;
  loadOut?: string | null;
}
/** Universal (whole-show) timing returned by /parse-ros. */
export interface RosUniversal {
  doors?: string | null;
  showEnd?: string | null;
  changeoverSupport?: number | null;
  changeoverHeadliner?: number | null;
}
export interface RosParseResult {
  rows: RosRow[];
  universal: RosUniversal;
}

/** POST /parse-ros, returns per-band rows + universal timing (mirrors parseTravelScreenshot). */
export async function parseRosScreenshot(imageBase64: string, lineup: TravelLineupHint[]): Promise<RosParseResult> {
  try {
    const r = await fetch(API_WORKER + '/parse-ros', {
      method: 'POST',
      headers: authHeaders('POST'),
      body: JSON.stringify({ image_base64: imageBase64, lineup }),
    });
    const j = await r.json() as { ok?: boolean; rows?: RosRow[]; universal?: RosUniversal };
    if (!r.ok) return { rows: [], universal: {} };
    return {
      rows: Array.isArray(j.rows) ? j.rows : [],
      universal: (j.universal && typeof j.universal === 'object') ? j.universal : {},
    };
  } catch { return { rows: [], universal: {} }; }
}

// ── Contract parse (Gemini via cntrl-api) — bulk-import a band's show history ──
// Mirrors parseRosScreenshot (same API_WORKER base, authHeaders, try/catch
// envelope). Feed a performance-contract PDF or image; Gemini returns the show
// fields (date/venue/lineup+roles/guarantee/ticket tiers/merch cuts/schedule).
export interface ParsedContractAct { name: string; role?: string; setTime?: string | null; setLength?: number | null }
export interface ParsedContract {
  date?: string | null;
  venue?: string | null;
  city?: string | null;
  promoter?: string | null;
  agency?: string | null;
  agent?: string | null;
  guarantee?: number | null;
  deposit?: number | null;
  doorSplit?: string | null;
  ageRestriction?: string | null;
  doors?: string | null;
  curfew?: string | null;
  lineup?: ParsedContractAct[];
  ticketTiers?: Array<{ name?: string; cap?: number; price?: number; sold?: number }>;
  merch?: { softPct?: number | null; recordedPct?: number | null };
  additionalProvisions?: string | null;
}

/** POST /parse-contract, returns the extracted show bundle or null. */
export async function parseContract(fileBase64: string, mimeType?: string): Promise<ParsedContract | null> {
  try {
    const r = await fetch(API_WORKER + '/parse-contract', {
      method: 'POST',
      headers: authHeaders('POST'),
      body: JSON.stringify({ file_base64: fileBase64, mime_type: mimeType }),
    });
    const j = await r.json() as { ok?: boolean; show?: ParsedContract };
    if (!r.ok || !j.ok) return null;
    return (j.show && typeof j.show === 'object') ? j.show : null;
  } catch { return null; }
}

// ── Contract → show matching + merge (bulk import, Ryan 2026-07-21) ────────────
const _normVenue = (v: string): string =>
  String(v || '').toLowerCase().replace(/\b(the|theatre|theater|club|hall|room|bar|lounge)\b/g, '').replace(/[^a-z0-9]+/g, ' ').trim();
const _normDate = (d: string): string => String(d || '').slice(0, 10);

export interface ContractMatch {
  parsed: ParsedContract;
  /** Matched existing show, or null when nothing lines up (flagged, not imported). */
  show: RawBookingEvent | null;
  score: number;   // 0..1 confidence
  reason: string;
}

/** Match a parsed contract to an existing show. Primary key = exact date (a band
 *  plays ~one show per date); venue similarity refines confidence and breaks
 *  same-date ties. A date miss = unmatched → the caller flags it for review
 *  (Ryan 2026-07-21: skip unmatched rather than create). */
export function matchContractToShow(parsed: ParsedContract, shows: ReadonlyArray<RawBookingEvent>): ContractMatch {
  const pd = _normDate(parsed.date || '');
  const pv = _normVenue(parsed.venue || '');
  if (!pd) return { parsed, show: null, score: 0, reason: 'no date on contract' };
  const sameDate = shows.filter((s) => _normDate(String(s.date || '')) === pd);
  if (!sameDate.length) return { parsed, show: null, score: 0, reason: 'no show on ' + pd };
  const venueHit = (s: RawBookingEvent): boolean => {
    const v = _normVenue(String(s.venue || ''));
    return !!(v && pv && (v === pv || v.includes(pv) || pv.includes(v)));
  };
  if (sameDate.length === 1) {
    const vOk = !pv || !_normVenue(String(sameDate[0].venue || '')) || venueHit(sameDate[0]);
    return { parsed, show: sameDate[0], score: vOk ? 1 : 0.7, reason: vOk ? 'date + venue' : 'date only (venue differs)' };
  }
  const byVenue = sameDate.find(venueHit);
  if (byVenue) return { parsed, show: byVenue, score: 1, reason: 'date + venue (multi-show day)' };
  return { parsed, show: sameDate[0], score: 0.5, reason: 'date only, ' + sameDate.length + ' shows that day' };
}

/** Apply a parsed contract onto an existing show. "Contract wins" on DEAL terms
 *  (guarantee, lineup+roles, ticket tiers, merch RATES, schedule, age, promoter),
 *  but only where the contract actually has a value. Live POS merch SALES and any
 *  door/settlement ACTUALS are preserved (Ryan 2026-07-21). Returns a NEW event;
 *  persist via saveFullShow (which also sorts the lineup headliner-first). */
export function applyContractToShow(base: RawBookingEvent, parsed: ParsedContract): RawBookingEvent {
  const ev: RawBookingEvent = { ...base };
  if (parsed.venue) ev.venue = parsed.venue;
  if (parsed.city) ev.city = parsed.city;
  if (parsed.promoter) ev.promoter = parsed.promoter;
  if (parsed.ageRestriction) ev.ageRestriction = parsed.ageRestriction;

  if (Array.isArray(parsed.ticketTiers) && parsed.ticketTiers.length) {
    ev.ticketTiers = parsed.ticketTiers.map((t) => ({ name: t.name || '', cap: Number(t.cap) || 0, price: Number(t.price) || 0 }));
    const cap = ev.ticketTiers.reduce((a, t) => a + (Number(t.cap) || 0), 0);
    if (cap > 0) ev.capacity = cap;
  }
  if (Array.isArray(parsed.lineup) && parsed.lineup.length) {
    ev.lineup = parsed.lineup.map((a) => ({
      name: (a.name || '').trim(),
      role: a.role || 'support',
      startTime: a.setTime || '',
      setLength: a.setLength != null ? String(a.setLength) : '',
    }));
  }
  if (parsed.guarantee != null) {
    const g = Number(parsed.guarantee) || 0;
    ev.financials = { ...(ev.financials || {}), guarantee: g, totalPay: g };
  }
  const dos = { ...(ev.dayOfShow || {}) };
  if (parsed.doors) dos.doors = parsed.doors;
  if (parsed.curfew) dos.showEnd = parsed.curfew;
  ev.dayOfShow = dos;

  const st: SettlementBlob = { ...(ev.settlement || {}) };
  const splits: SettlementSplits = { ...(st.splits || {}) };
  if (parsed.guarantee != null) splits.guarantee = Number(parsed.guarantee) || 0;
  if (parsed.deposit != null) splits.deposit = Number(parsed.deposit) || 0;
  st.splits = splits;
  const pm = parsed.merch;
  if (pm && (pm.softPct != null || pm.recordedPct != null)) {
    const prev: SettlementMerchCut = st.merch_cut || {};
    const soft = Number(pm.softPct) || 0;
    const rec = Number(pm.recordedPct) || 0;
    st.merch_cut = {
      ...prev,                       // preserve POS sales_amount / sales_source / last_pulled_at
      enabled: soft > 0 || rec > 0,
      percentage: soft,
      percentage_recorded: rec,
    };
  }
  ev.settlement = st;

  const extra = [
    parsed.doorSplit,
    parsed.additionalProvisions,
    parsed.agency ? ('Agency: ' + parsed.agency + (parsed.agent ? ' · ' + parsed.agent : '')) : '',
  ].filter(Boolean).join(' · ');
  if (extra) ev.terms = extra;
  return ev;
}

// ── Settlement import (Prism settlement PDF → settlement blob, Ryan 2026-07-22) ──
export interface ParsedSettlementTier { name: string; sold?: number; price?: number; comps?: number | null; date?: string | null }
export interface ParsedSettlementExpense { category: string; group?: string; amount?: number; budget?: number | null; date?: string | null }
export interface ParsedSettlement {
  date?: string | null;
  /** multi-night 2026-07-30 — every date the sheet covers; null/1-entry = normal. */
  dates?: string[] | null;
  venue?: string | null; city?: string | null; artist?: string | null;
  buyer?: string | null; buyer_email?: string | null;
  guarantee?: number | null; deposit?: number | null;
  door_split_pct?: number | null; split_basis?: string | null; tax_withholding_pct?: number | null;
  gross?: number | null; net_gross?: number | null; net_revenue_to_split?: number | null;
  profit_loss?: number | null; attendance?: number | null; artist_payout?: number | null;
  tiers?: ParsedSettlementTier[]; expenses?: ParsedSettlementExpense[];
}

export async function parseSettlement(fileBase64: string, mimeType?: string): Promise<ParsedSettlement | null> {
  try {
    const r = await fetch(API_WORKER + '/parse-settlement', {
      method: 'POST', headers: authHeaders('POST'),
      body: JSON.stringify({ file_base64: fileBase64, mime_type: mimeType }),
    });
    const j = await r.json() as { ok?: boolean; settlement?: ParsedSettlement };
    if (!r.ok || !j.ok) return null;
    return (j.settlement && typeof j.settlement === 'object') ? j.settlement : null;
  } catch { return null; }
}

/* ── Multi-night settlements (multi-night 2026-07-30, Ryan: "we need to be
   able to handle ones like this with multiple dates" — the Relient K sheet:
   two nights on one settlement, per-night ticket lines, shared hospitality,
   one combined payout).

   Strategy: EXPLODE the parsed sheet into one per-night ParsedSettlement and
   feed each through the existing match/apply unchanged. Allocation:
     · tiers tagged with a date → their night; untagged tiers on a multi-date
       sheet land on the FIRST night (sheets that split by night tag every
       line — untagged is the degenerate case, and first-night beats
       double-counting).
     · per-night gross/attendance = that night's tiers (sold × price).
     · date-tagged expenses → their night; untagged (shared) expenses AND the
       combined money fields (gross, net_gross, payout, P/L) split
       proportional to each night's tier gross — accounting-correct for a
       %-of-net deal. Even split only when tier gross is unknowable.
     · flat guarantee/deposit split EVENLY across nights; percentages copy. */
export function explodeMultiDateSettlement(parsed: ParsedSettlement): ParsedSettlement[] {
  const dates = (Array.isArray(parsed.dates) ? parsed.dates : [])
    .map((d) => String(d || '').slice(0, 10))
    .filter((d) => /^\d{4}-\d{2}-\d{2}$/.test(d));
  const uniq = [...new Set(dates)];
  if (uniq.length < 2) return [parsed];

  const tiers = Array.isArray(parsed.tiers) ? parsed.tiers : [];
  const expenses = Array.isArray(parsed.expenses) ? parsed.expenses : [];
  const tierDate = (t: ParsedSettlementTier): string => {
    const d = String(t.date || '').slice(0, 10);
    return uniq.includes(d) ? d : uniq[0];
  };
  const grossOf = (arr: ParsedSettlementTier[]): number =>
    arr.reduce((a, t) => a + (Number(t.sold) || 0) * (Number(t.price) || 0), 0);

  const perTiers = uniq.map((d) => tiers.filter((t) => tierDate(t) === d));
  const perGross = perTiers.map(grossOf);
  const totalGross = perGross.reduce((a, b) => a + b, 0);
  /* Proportional share of a combined figure; even split when tier gross is 0. */
  const share = (v: number | null | undefined, i: number): number | null => {
    if (v == null) return null;
    const f = totalGross > 0 ? perGross[i] / totalGross : 1 / uniq.length;
    return Math.round(Number(v) * f * 100) / 100;
  };

  return uniq.map((d, i) => {
    const nightExpenses: ParsedSettlementExpense[] = [];
    for (const e of expenses) {
      if (!e) continue;
      const ed = String(e.date || '').slice(0, 10);
      if (uniq.includes(ed)) {
        if (ed === d) nightExpenses.push(e);
      } else {
        /* shared line → this night's proportional slice, label kept */
        const amt = share(e.amount, i);
        nightExpenses.push({ ...e, amount: amt == null ? undefined : amt });
      }
    }
    const n = uniq.length;
    return {
      ...parsed,
      date: d,
      dates: [d],
      tiers: perTiers[i],
      expenses: nightExpenses,
      gross: share(parsed.gross, i),
      net_gross: share(parsed.net_gross, i),
      net_revenue_to_split: share(parsed.net_revenue_to_split, i),
      profit_loss: share(parsed.profit_loss, i),
      artist_payout: share(parsed.artist_payout, i),
      attendance: perTiers[i].length
        ? perTiers[i].reduce((a, t) => a + (Number(t.sold) || 0), 0)
        : share(parsed.attendance, i),
      guarantee: parsed.guarantee != null ? Math.round((Number(parsed.guarantee) / n) * 100) / 100 : null,
      deposit: parsed.deposit != null ? Math.round((Number(parsed.deposit) / n) * 100) / 100 : null,
    };
  });
}

export interface SettlementMatch { parsed: ParsedSettlement; show: RawBookingEvent | null; score: number; reason: string }
/** Match a parsed settlement to an existing show — same date-primary, venue-refine
 *  logic as the contract matcher. */
export function matchSettlementToShow(parsed: ParsedSettlement, shows: ReadonlyArray<RawBookingEvent>): SettlementMatch {
  const c = matchContractToShow({ date: parsed.date || null, venue: parsed.venue || null } as ParsedContract, shows);
  return { parsed, show: c.show, score: c.score, reason: c.reason };
}

/** Apply a parsed settlement onto an existing show: fills tickets SOLD per tier,
 *  the door buckets (so the gross math ties out), itemized expenses (actual +
 *  fixed/variable), guarantee/deposit/split/tax, and marks it settled. Contract
 *  deal terms already on the show are preserved unless the settlement restates
 *  them. Returns a NEW event; persist via saveFullShow. */
export function applySettlementToShow(base: RawBookingEvent, parsed: ParsedSettlement): RawBookingEvent {
  const ev: RawBookingEvent = { ...base };
  if (parsed.venue) ev.venue = parsed.venue;
  if (parsed.city) ev.city = parsed.city;
  if (parsed.buyer) ev.promoter = parsed.buyer;

  const tiers = Array.isArray(parsed.tiers) ? parsed.tiers.filter((t) => t && (t.name || t.price != null)) : [];
  if (tiers.length) {
    const prevTiers = Array.isArray(ev.ticketTiers) ? ev.ticketTiers : [];
    ev.ticketTiers = tiers.map((t) => {
      const nm = String(t.name || '').trim();
      const prev = prevTiers.find((pt) => String(pt.name || '').trim().toLowerCase() === nm.toLowerCase());
      const sold = Number(t.sold) || 0;
      const price = Number(t.price) || 0;
      const cap = (prev && Number(prev.cap)) ? Number(prev.cap) : sold;
      return { name: nm, price, sold, cap };
    });
  }

  const st: SettlementBlob = { ...(ev.settlement || {}) };
  /* Clean up any venue expenses a PRIOR import left in the band's list — those
     have no id (band-added expenses always do), so drop the id-less ones. */
  if (Array.isArray(st.expenses)) st.expenses = st.expenses.filter((e) => e && e.id);
  if (st.show_expenses) delete (st as { show_expenses?: unknown }).show_expenses;
  /* Door buckets by tier NAME, not price (Ryan 2026-07-23). Price-grouping
     inflated gross badly: a $12.50 fan-presale set the presale bucket while
     EVERY other ticket (397 × $25 advance…) landed in walk-up at the sheet's
     MAX price ($55 table seating) — $11,690 real became $24,215 fake. Now:
     day-of/walk-up tiers → walkup, VIP tiers → vip, everything else →
     presale, each at its sold-weighted average price so bucket sold × price
     reproduces the sheet's gross to the cent (± rounding). */
  const isVip = (n: string): boolean => /vip/i.test(n);
  const isDos = (n: string): boolean => /day\s*of|\bdos\b|walk[\s-]?up|at\s+the\s+door/i.test(n);
  const vip = tiers.filter((t) => isVip(String(t.name || '')));
  const dosTiers = tiers.filter((t) => !isVip(String(t.name || '')) && isDos(String(t.name || '')));
  const advTiers = tiers.filter((t) => !isVip(String(t.name || '')) && !isDos(String(t.name || '')));
  const sumSold = (arr: ParsedSettlementTier[]): number => arr.reduce((a, t) => a + (Number(t.sold) || 0), 0);
  const sumGross = (arr: ParsedSettlementTier[]): number => arr.reduce((a, t) => a + (Number(t.sold) || 0) * (Number(t.price) || 0), 0);
  const avgPrice = (arr: ParsedSettlementTier[]): number => { const n = sumSold(arr); return n > 0 ? Math.round((sumGross(arr) / n) * 100) / 100 : 0; };
  const door = { ...(st.door || {}) };
  if (tiers.length) {
    door.presale_sold = sumSold(advTiers);
    door.presale_price = avgPrice(advTiers);
    door.walkup_sold = sumSold(dosTiers);
    door.walkup_price = avgPrice(dosTiers);
    door.vip_sold = sumSold(vip);
    door.vip_price = avgPrice(vip) || (vip.length ? (Number(vip[0].price) || 0) : (door.vip_price || 0));
    const comps = tiers.reduce((a, t) => a + (Number(t.comps) || 0), 0);
    if (comps > 0) door.comps_in = comps;
  }
  st.door = door;

  const splits: SettlementSplits = { ...(st.splits || {}) };
  if (parsed.guarantee != null) splits.guarantee = Number(parsed.guarantee) || 0;
  if (parsed.deposit != null) splits.deposit = Number(parsed.deposit) || 0;
  if (parsed.door_split_pct != null) splits.door_split_pct = Number(parsed.door_split_pct) || 0;
  if (parsed.tax_withholding_pct != null) splits.tax_withholding_pct = Number(parsed.tax_withholding_pct) || 0;
  st.splits = splits;

  /* Artist side does NOT retain the venue's cost breakdown (Ryan 2026-07-22:
     'the artist side doesn't need any of the venue cost'). Only a single total
     is kept in the hidden reference block for the venue bridge / reconciliation. */
  const expTotal = Array.isArray(parsed.expenses)
    ? parsed.expenses.reduce((a, e) => a + (Number(e && e.amount) || 0), 0) : 0;
  const grossRef = parsed.gross != null ? Number(parsed.gross) || 0
    : (Array.isArray(parsed.tiers) ? parsed.tiers.reduce((a, t) => a + (Number(t.sold) || 0) * (Number(t.price) || 0), 0) : 0);
  const payoutRef = parsed.artist_payout != null ? Number(parsed.artist_payout) || 0 : 0;
  /* Settle the split base robustly: the pre-computed pool if the parser gave it,
     else gross minus the show expenses (both of which the parse DOES extract).
     Without this the artist side would take 42.5% of the full GROSS and overstate
     the door share past the guarantee. (Ryan 2026-07-22) */
  const splitBaseRef = parsed.net_revenue_to_split != null ? Number(parsed.net_revenue_to_split) || 0
    : (grossRef > 0 ? Math.round((grossRef - expTotal) * 100) / 100 : 0);
  if (splitBaseRef > 0) splits.settled_split_base = splitBaseRef;
  st.reference = {
    source: 'prism',
    gross: grossRef || undefined,
    net_gross: parsed.net_gross != null ? Number(parsed.net_gross) || 0 : undefined,
    net_revenue_to_split: parsed.net_revenue_to_split != null ? Number(parsed.net_revenue_to_split) || 0 : undefined,
    profit_loss: parsed.profit_loss != null ? Number(parsed.profit_loss) || 0 : undefined,
    attendance: parsed.attendance != null ? Number(parsed.attendance) || 0 : undefined,
    artist_payout: payoutRef || undefined,
    expenses_total: expTotal || undefined,
    venue_net: (grossRef && (expTotal || payoutRef)) ? Math.round((grossRef - expTotal - payoutRef) * 100) / 100 : undefined,
  };
  st.status = 'settled';
  ev.settlement = st;

  if (parsed.guarantee != null) {
    const g = Number(parsed.guarantee) || 0;
    ev.financials = { ...(ev.financials || {}), guarantee: g };
  }
  return ev;
}

// ── VIP list parse / stringify (verbatim parseVipList / vipListToText) ─────────
export function parseVipList(text: string): VipRecord[] {
  const out: VipRecord[] = [];
  String(text || '').split(/\r?\n/).forEach((raw) => {
    const line = String(raw || '').trim();
    if (!line) return;
    let name = line, email = '';
    const bracket = line.match(/^(.*?)\s*<([^>]+)>\s*$/);
    if (bracket) {
      name = bracket[1].trim();
      email = bracket[2].trim();
    } else {
      const parts = line.split(/\s*[,|\t]\s*/);
      if (parts.length >= 2 && /@/.test(parts[parts.length - 1])) {
        email = (parts.pop() as string).trim();
        name = parts.join(', ').trim();
      }
    }
    if (!email && /^[^\s,]+@[^\s,]+\.[^\s,]+$/.test(line)) { email = line; name = line; }
    if (!name && !email) return;
    out.push({ name: name || email, email: email || '' });
  });
  return out;
}
export function vipListToText(list: ReadonlyArray<VipRecord> | undefined): string {
  if (!Array.isArray(list)) return '';
  return list.map((v) => {
    if (!v) return '';
    const n = v.name || '', e = v.email || '';
    if (n && e && n !== e) return n + ', ' + e;
    return n || e;
  }).filter(Boolean).join('\n');
}

// ── Stage-schedule time cascade (verbatim parseTimeStr / formatTimeMin) ────────
export function parseTimeStr(s: string | undefined): number | null {
  if (!s) return null;
  const t = String(s).trim().toLowerCase().replace(/\s+/g, '');
  if (!t) return null;
  const m = t.match(/^(\d{1,2})(?::(\d{2}))?\s*(am|pm)?$/);
  if (!m) return null;
  let h = parseInt(m[1], 10);
  const mins = m[2] ? parseInt(m[2], 10) : 0;
  const ap = m[3];
  if (h > 23 || mins > 59) return null;
  if (ap === 'pm' && h < 12) h += 12;
  else if (ap === 'am' && h === 12) h = 0;
  else if (!ap && h < 8) h += 12;
  return h * 60 + mins;
}
export function formatTimeMin(min: number | null): string {
  if (min == null || isNaN(min)) return '';
  const n = ((min % (24 * 60)) + 24 * 60) % (24 * 60);
  const h = Math.floor(n / 60), m = n % 60;
  const ap = h >= 12 ? 'pm' : 'am';
  let hh = h % 12; if (hh === 0) hh = 12;
  return m === 0 ? (hh + ap) : (hh + ':' + (m < 10 ? '0' + m : m) + ap);
}

// ════════════════════════════════════════════════════════════════════════════
// DAY OF SHOW, run-of-show "pass sheet" row + header builders.
//
// Faithful port of schedule-wiring.js dayOfShowHtml / _buildDayOfShowPDF row
// construction (the [time, label] pairs: LOAD IN → per-act SOUNDCHECK → DOORS →
// per-act SET → LOAD-OUT lines) and the headliner-up title logic
// (dosCityState / headliner pick / "W/ SUPPORT FROM …" / venue line / date).
//
// THE ONE INTENTIONAL CHANGE FROM LEGACY: the rows are sorted ASCENDING by the
// parsed start clock time. Legacy renders sets in reversed lineup order, which
// puts them out of chronological order (the bug Ryan flagged). Rows whose time
// can't be parsed (empty / label-only load-out lines) fall to the END, in a
// STABLE order (preserving the legacy push order among themselves). The label
// text, the time-range strings, and everything else are byte-identical to legacy.
// ════════════════════════════════════════════════════════════════════════════

/** Verbatim port of dosCityState(ev), city, else parse it out of venueAddress. */
function dosCityState(ev: RawBookingEvent): string {
  if (ev.city) return String(ev.city);
  if (!ev.venueAddress) return '';
  const parts = String(ev.venueAddress).split(',').map((s) => s.trim());
  if (parts.length >= 3) return parts[parts.length - 3] + ', ' + parts[parts.length - 2].replace(/\s*\d{5}.*/, '');
  if (parts.length >= 2) return parts[parts.length - 2].replace(/\s*\d{5}.*/, '');
  return '';
}

/** Verbatim port of dosTimeRangeStr, "<start>" or "<start> - <end>". */
function dosTimeRangeStr(startStr: string | undefined, lengthMin: string | number | undefined): string {
  const s = parseTimeStr(startStr);
  if (s == null) return clock12(startStr || ''); // time-12h 2026-07-05
  const len = parseInt(String(lengthMin), 10) || 0;
  if (!len) return formatTimeMin(s);
  return formatTimeMin(s) + ' - ' + formatTimeMin(s + len);
}

// time-12h 2026-07-05, display normalizer for raw stored DOS clock strings
// (doors / load-in / load-out lines): a 24h "HH:MM" ("17:00") renders "5:00 PM";
// anything already 12h or free-text passes through untouched. Display-only,
// parseTimeStr stays the sort/parse authority (its afternoon heuristic must NOT
// be used for display: it would turn "07:00" into 7 PM).
function clock12(s: string): string {
  const str = String(s ?? '').trim();
  if (!str) return '';
  const m = str.match(/^(\d{1,2}):(\d{2})$/);
  if (!m) return str;
  const hh = parseInt(m[1], 10);
  if (isNaN(hh) || hh > 23) return str;
  const period = hh >= 12 ? 'PM' : 'AM';
  let h12 = hh % 12;
  if (h12 === 0) h12 = 12;
  return h12 + ':' + m[2] + ' ' + period;
}

/** Verbatim port of _dosLoadInStr, resolves a flat string OR per-band map. */
function dosLoadInStr(dos: NonNullable<RawBookingEvent['dayOfShow']>, ev: RawBookingEvent): string {
  const raw = dos && dos.loadIn;
  if (typeof raw === 'string') return raw.trim();
  if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
    let myId = '';
    try {
      const u = (window as unknown as { ac?: { user?: { bandPrimaryId?: string; band_primary_id?: string; id?: string } } }).ac?.user || null;
      if (u) myId = String(u.bandPrimaryId || u.band_primary_id || u.id || '');
    } catch { /* ignore */ }
    const hostId = String((ev && ev._sharedHostBandId) || '');
    const map = raw as Record<string, string>;
    let pick = (myId && map[myId]) || (hostId && map[hostId]) || '';
    if (!pick) { for (const k in map) { if (map[k]) { pick = map[k]; break; } } }
    return String(pick || '').trim();
  }
  return '';
}

/** One run-of-show row (a [time, label] pair, time may be a range or ''). */
export interface DosRow { readonly time: string; readonly label: string }

/**
 * Build the run-of-show rows for the Day of Show pass sheet, faithful to the
 * legacy LOAD IN → SOUNDCHECK → DOORS → SET → LOAD-OUT order/labels, BUT sorted
 * ascending by parsed start time (the legacy bug-fix). Unparseable/empty-time
 * rows go last, stable.
 */
export function buildDosRows(ev: RawBookingEvent): DosRow[] {
  const dos = ev.dayOfShow || {};
  const lineup: LineupAct[] = Array.isArray(ev.lineup)
    ? (ev.lineup.filter((a): a is LineupAct => typeof a !== 'string'))
    : [];

  // Each entry carries a sort key parsed from the leading time (null ⇒ goes last).
  const entries: Array<{ row: DosRow; sort: number | null; seq: number }> = [];
  let seq = 0;
  const push = (time: string, label: string): void => {
    // Sort by the FIRST clock token (the start time), handles "8pm - 8:45pm".
    const head = String(time || '').split('-')[0].split('-')[0].trim();
    const sort = parseTimeStr(head);
    entries.push({ row: { time, label }, sort, seq: seq++ });
  };

  // LOAD IN, flat field, per-band map, or a leading time off loadInProd line 1.
  let loadInTime = dosLoadInStr(dos, ev);
  if (!loadInTime && dos.loadInProd) {
    const firstLine = String(dos.loadInProd).split('\n')[0] || '';
    const m = firstLine.match(/^([\d:apm\s-]+(?:[ap]m)?)/i);
    if (m) loadInTime = m[1].trim();
  }
  if (loadInTime) push(clock12(loadInTime), 'LOAD IN'); // time-12h 2026-07-05

  // Per-act SOUNDCHECK rows (lineup order).
  lineup.forEach((a) => {
    if (!a.soundcheckStart && !a.soundcheckLength) return;
    const range = dosTimeRangeStr(a.soundcheckStart, a.soundcheckLength);
    if (!range) return;
    push(range, String(a.name || '').toUpperCase());
  });

  // DOORS.
  if (dos.doors) push(clock12(String(dos.doors)), 'DOORS'); // time-12h 2026-07-05

  // Per-act SET rows. Legacy pushed these in REVERSED lineup order (the bug);
  // we push in lineup order and let the chronological sort below order them.
  lineup.forEach((a) => {
    if (!a.startTime && !a.setLength) return;
    const range = dosTimeRangeStr(a.startTime, a.setLength);
    push(range, String(a.name || '').toUpperCase());
  });

  // LOAD-OUT lines, split each on a leading "<time> <label>", else label-only.
  String(dos.loadOutNotes || dos.loadOut || '').split('\n')
    .map((s) => s.trim()).filter(Boolean)
    .forEach((line) => {
      const lm = line.match(/^([\d:apm\s-]+(?:[ap]m)?)[\s:-]+(.+)$/i);
      if (lm) push(clock12(lm[1].trim()), lm[2].trim().toUpperCase()); // time-12h 2026-07-05
      else push('', line.toUpperCase());
    });

  // Sort ascending by parsed start time; null-time rows go last; stable within.
  entries.sort((a, b) => {
    if (a.sort == null && b.sort == null) return a.seq - b.seq;
    if (a.sort == null) return 1;
    if (b.sort == null) return -1;
    return (a.sort - b.sort) || (a.seq - b.seq);
  });
  return entries.map((e) => e.row);
}

/** The pass-sheet header block (headliner / support / venue / date strings). */
export interface DosHeader {
  readonly headliner: string;
  readonly supportLine: string;
  readonly venueLine: string;
  readonly dateShort: string;
}

/**
 * Build the Day of Show header block, verbatim legacy logic: headliner = the
 * act with role==='headliner' (else the first act, else ev.name); support =
 * everyone else → "W/ SUPPORT FROM …"; venue line = ev.name + city/state; date =
 * "Month Day, Year". All strings are returned in source case (the modal/PDF
 * upper-case them, matching legacy escapeHtml(...).toUpperCase()).
 */
export function dosHeader(ev: RawBookingEvent): DosHeader {
  const lineup: LineupAct[] = Array.isArray(ev.lineup)
    ? (ev.lineup.filter((a): a is LineupAct => typeof a !== 'string'))
    : [];
  const cityState = dosCityState(ev);

  /* Co-headline (Ryan 2026-07-22): a bill can have two (or more) top acts —
     Mae & The Spill Canvas. Treat headliner + co_headliner as shared top billing
     so the pass sheet reads "Mae & The Spill Canvas", and everyone else is
     support. Falls back to the sole headliner, then the first act. */
  const tops = lineup.filter((a) => a && a.name && (a.role === 'headliner' || a.role === 'co_headliner'));
  let headliner: LineupAct | null = tops.length ? tops[0] : null;
  if (!headliner && lineup.length) headliner = lineup[0];
  const topActs = tops.length ? tops : (headliner ? [headliner] : []);
  const headlinerName = topActs.length ? topActs.map((a) => a.name).join(' & ') : (ev.name || 'Untitled');
  const support = lineup.filter((a) => a && a.name && topActs.indexOf(a) === -1);
  const supportLine = support.length
    ? ('W/ SUPPORT FROM ' + support.map((s) => s.name).join(', '))
    : '';
  const venueLine = ((ev.name || '') + (cityState ? ' ' + cityState : ''))
    .replace(/\s+,/g, ',').trim();

  let dateShort = '';
  const d = new Date(String(ev.date) + 'T00:00:00');
  try {
    dateShort = d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  } catch { dateShort = String(ev.date || ''); }

  return { headliner: headlinerName, supportLine, venueLine, dateShort };
}

// ── Travel cost totals (verbatim travelTotals) ────────────────────────────────
export function travelTotals(items: ReadonlyArray<TravelItem>): { flights: number; hotel: number } {
  const totals = { flights: 0, hotel: 0 };
  (items || []).forEach((t) => {
    const n = Number(t && t.cost);
    if (!isFinite(n) || n <= 0) return;
    if (t.type === 'flight') totals.flights += n;
    else if (t.type === 'hotel') totals.hotel += n;
  });
  return totals;
}

// ── Save the full show (verbatim mirror of saveFormToEvent payload) ───────────
/** Strip the editing-only `_autoStart` flag before persisting a lineup act. */
function cleanAct(a: LineupAct): LineupAct {
  return {
    name: (a.name || '').trim(),
    role: a.role || 'opener',
    bit_id: a.bit_id || null,
    bit_img: a.bit_img || null,
    startTime: String(a.startTime || '').trim(),
    setLength: String(a.setLength || '').trim(),
    soundcheckStart: String(a.soundcheckStart || '').trim(),
    soundcheckLength: String(a.soundcheckLength || '').trim(),
    // Per-band Run-of-Show times (NEW): load in + load out per act → DOS PDF.
    loadIn: String(a.loadIn || '').trim(),
    loadOut: String(a.loadOut || '').trim(),
  };
}

/**
 * Persist a fully-built show event into the schedule blob (read-modify-write,
 * PUT {value}). The caller (EditShowModal) has already assembled `ev` with the
 * exact field shape legacy saveFormToEvent produces, this is the network write
 * + upsert that legacy's saveSchedule()/_doPutSchedule do. Returns the event id.
 */
/* tour-threads 2026-07-27 (Ryan: "band thread per show… and per tour if
   theres a tour — 20 dates, 1 thread"). The worker's POST /api/show-threads
   has been orphaned since the react cutover (nothing called it), so this is
   the ONE creation choke point: every show save funnels through here.
   · Show on a tour  → ensure the single tour rollup thread
     (show_id 'tour:<slug>', is_tour, never locks, never swept).
   · Standalone show → ensure the per-show thread (date+7d lock, 14d sweep).
   Members default server-side to the whole team. Fire-and-forget — a thread
   hiccup must never fail a schedule save. */
export function ensureShowThread(ev: RawBookingEvent): void {
  try {
    const id = String(ev.id ?? '').trim();
    if (!id) return;
    const isConfirmed = ev.confirmed === true || String(ev.status || '') === 'confirmed';
    /* hold-no-chat 2026-07-28 (Ryan: "Chat shouldn't happen until a show is
       confirmed") — REGRESSION FIX. ensureShowThread landed in 1827405 and was
       wired into saveFullShow, which runs on EVERY save, so a HOLD minted a
       band thread the moment it was saved. Before that commit POST
       /api/show-threads was orphaned (nothing called it) and holds created no
       thread at all — which is exactly why this looked like new behaviour.
       `isConfirmed` was already computed and handed to the worker, but the
       worker only uses it to set member status (accepted vs pending); it never
       gated creation. Gating here covers every caller at once: saveFullShow,
       createTourShows, the Pro per-team branch below, and mobile's new-show
       screen. The confirm path is unaffected — confirmShowAndNotify stamps
       confirmed:true and then calls saveFullShow, so the thread appears the
       moment the show is confirmed, matching the inbox empty-state copy
       ("Confirmed shows auto-create a thread"). */
    if (!isConfirmed) return;
    const tour = String(ev.tourName || '').trim();
    const date = String(ev.date || '').trim();
    /* acpro-team-chats 2026-07-28 (Ryan: "no matter if its a one off show or
       a 6 month tour, A chat gets made for every team with 2 or more people
       in it") — Pro accounts get PER-TEAM chats instead of the one band-wide
       thread. Standard AC keeps the single thread below. */
    const _u = getCachedUser();
    if (_u && isPro(_u)) { void ensureProTeamThreads(ev); return; }
    if (tour) {
      const slug = tour.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '').slice(0, 60) || 'tour';
      void apiPost('auth', '/api/show-threads', {
        show_id: 'tour:' + slug,
        show_name: tour,
        show_date: date,
        show_venue: '',
        is_tour: true,
        confirmed: isConfirmed,
      }).catch(() => { /* non-blocking */ });
    } else if (date) {
      void apiPost('auth', '/api/show-threads', {
        show_id: id,
        show_name: String(ev.name || ''),
        show_date: date,
        show_venue: String(ev.venue || ''),
        is_tour: false,
        confirmed: isConfirmed,
      }).catch(() => { /* non-blocking */ });
    }
  } catch { /* never block a save */ }
}

/* acpro-team-chats 2026-07-28 — one thread per Pro team with ≥2 LINKED
   members. show_id gets a ':<team>' suffix so each team chat is its own
   idempotent row; strict_members keeps the saving user out of chats for
   teams they're not on. Membership merges on re-save (new team members
   join on the next show edit); it does not yet UN-join someone who
   changes teams mid-run — flagged, same reconcile pattern as bus chats
   if Ryan wants it. */
async function ensureProTeamThreads(ev: RawBookingEvent): Promise<void> {
  try {
    const id = String(ev.id ?? '').trim();
    const isConfirmed = ev.confirmed === true || String(ev.status || '') === 'confirmed';
    const tour = String(ev.tourName || '').trim();
    const date = String(ev.date || '').trim();
    if (!id || (!tour && !date)) return;
    const crew = await fetchCrew().catch(() => []);
    const slug = tour ? (tour.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '').slice(0, 60) || 'tour') : '';
    const baseId = tour ? 'tour:' + slug : id;
    const baseName = tour || String(ev.name || 'Show');
    for (const t of PRO_TEAMS) {
      const members = crew
        .filter((c) => !c.pending && c.userId && teamOf(c) === t.key)
        .map((c) => c.userId);
      if (members.length < 2) continue;
      void apiPost('auth', '/api/show-threads', {
        show_id: baseId + ':' + t.key,
        show_name: baseName + ' — ' + t.label,
        show_date: date,
        show_venue: tour ? '' : String(ev.venue || ''),
        is_tour: !!tour,
        confirmed: isConfirmed,
        strict_members: true,
        member_user_ids: members,
      }).catch(() => { /* non-blocking */ });
    }
  } catch { /* never block a save */ }
}

export async function saveFullShow(ev: RawBookingEvent): Promise<void> {
  const lineup = (Array.isArray(ev.lineup) ? ev.lineup : [])
    .filter((a): a is LineupAct => typeof a !== 'string' && !!a && !!(a.name && a.name.trim()))
    .map(cleanAct);
  // Canonical billing order: headliner → direct support → support → opener →
  // local (Ryan 2026-07-21). Run-of-Show re-sorts by set time, so this only
  // drives billing displays. Array.sort is stable → manual order kept within a role.
  const ROLE_RANK: Record<string, number> = { headliner: 0, co_headliner: 0, direct_support: 1, support: 2, opener: 3, local_support: 4 };
  lineup.sort((a, b) => (ROLE_RANK[(a.role as string) || 'opener'] ?? 3) - (ROLE_RANK[(b.role as string) || 'opener'] ?? 3));
  const out: RawBookingEvent = { ...ev, lineup };
  await saveShow(out);
  ensureShowThread(out);
}

/* ═══════════════════════════════════════════════════════════════════════════
   SHOW VISIBILITY (show-scope 2026-07-29)

   Ryan: "only AV seats should see everything on a calendar, everyone else
   should only see the events they are invited to", and invited means
   "selected in the members section" when the show was created.

   ⚠ DISPLAY ONLY. Never filter the array a writer touches. saveShow(),
   createTourShows() and the merch sync all read the WHOLE schedule blob,
   splice, and PUT it back. Hand one of them a filtered array and saving a
   single show silently deletes every show that member could not see. The
   filter belongs at the render site, downstream of the fetch — the opposite
   of how view-as is applied in lib/api.ts, and for this exact reason.

   Matching is userId → email → display name, mirroring inviteMembersToHold's
   own resolution order. A memberContacts row carrying none of the three
   cannot be matched to anyone and simply never grants visibility.
   ═══════════════════════════════════════════════════════════════════════ */

/* Kept local rather than imported from mobile/lib/mobilePerms so lib/ does not
   depend on mobile/. Same set as isAvTierRole there — change both together. */
const SHOW_ALL_ROLES: Record<string, 1> = { jedi: 1, admin: 1, artist: 1, band_member_av: 1 };

/** True when this seat sees every date on the band's calendar. */
export function canSeeAllShows(user?: User | null): boolean {
  const u = (user === undefined ? getCachedUser() : user) as unknown as
    { role?: string; membership_role?: string } | null;
  if (!u) return false;
  const role = String(u.membership_role || u.role || '').toLowerCase();
  return SHOW_ALL_ROLES[role] === 1;
}

/** Minimal shape the visibility check reads. Deliberately NOT a generic
 *  constraint: CalEvent / ScheduleEvent / UpcomingShow don't all declare
 *  memberContacts even though the blob carries it, and constraining on it
 *  would reject them at the call site. Read structurally, narrow nothing. */
interface ShowVisibilityFields {
  memberContacts?: ReadonlyArray<MemberContact> | null;
}

/** True when `user` was named in this show's Members section. */
export function isOnShow(ev: unknown, user?: User | null): boolean {
  const row = ev as ShowVisibilityFields | null | undefined;
  if (!row) return false;
  const u = (user === undefined ? getCachedUser() : user) as unknown as
    { id?: string | number; email?: string; display_name?: string } | null;
  if (!u) return false;
  const myId = u.id != null ? String(u.id) : '';
  const myEmail = String(u.email || '').toLowerCase();
  const myName = String(u.display_name || '').trim().toLowerCase();
  const members = Array.isArray(row.memberContacts) ? row.memberContacts : [];
  for (const m of members) {
    if (!m) continue;
    if (myId && m.userId != null && String(m.userId) === myId) return true;
    if (myEmail && m.email && String(m.email).toLowerCase() === myEmail) return true;
    if (myName) {
      const nm = ((m.firstName || '') + ' ' + (m.lastName || '')).trim().toLowerCase();
      if (nm && nm === myName) return true;
    }
  }
  return false;
}

/** Narrow a schedule array to what this seat may see. DISPLAY ONLY. */
export function visibleShows<T>(
  events: ReadonlyArray<T>,
  user?: User | null,
): T[] {
  const arr = Array.isArray(events) ? events.slice() : [];
  const who = user === undefined ? getCachedUser() : user;
  if (canSeeAllShows(who)) return arr;
  /* Ryan chose strict: a show with an EMPTY members list is not "everyone's",
     it is nobody's yet. Non-AV seats see nothing until they are named. Flagged
     at the time: this empties most non-AV calendars until Members is
     backfilled, and a non-AV booking agent cannot see a hold they created
     unless they add themselves to it. */
  return arr.filter((ev) => isOnShow(ev, who));
}

/** True when an event id already exists in the schedule blob (was an edit). */
export async function showExists(id: string | number | undefined): Promise<boolean> {
  if (id == null || id === '') return false;
  const res = await apiGet<ScheduleResponse>('auth', '/api/data/schedule').catch(() => null);
  const arr = unwrapArr<RawBookingEvent>(res);
  return arr.some((x) => String(x.id) === String(id));
}

// ── Confirm a hold + auto-apply members + fire the "Show confirmed" push ───────
//
// Verbatim mirror of confirmAndAutoApplyMembers: stamps every pending member's
// response = 'yes' (respecting explicit no/pass), pulls the show_hold opp, saves
// the schedule, then POSTs /api/notify/show-confirmed to the newly-confirmed
// members' user ids. Push is best-effort.
export async function confirmShowAndNotify(ev: RawBookingEvent): Promise<RawBookingEvent> {
  const out: RawBookingEvent = { ...ev, confirmed: true, status: 'confirmed' };
  const members = (out.memberContacts || []).filter((m) => m && (m.firstName || m.lastName));
  const responses: Record<string, string> = { ...(out.responses || {}) };
  const newlyConfirmedUserIds: string[] = [];
  members.forEach((m) => {
    const nm = ((m.firstName || '') + ' ' + (m.lastName || '')).trim();
    if (!nm) return;
    const cur = responses[nm];
    if (cur === 'no' || cur === 'pass' || cur === 'yes' || cur === 'in') return;
    responses[nm] = 'yes';
    if (m.userId) newlyConfirmedUserIds.push(String(m.userId));
  });
  out.responses = responses;

  // Pull the show_hold opp so it stops surfacing in pending feeds.
  const holdOppId = 'show_hold:' + out.id;
  const oppCleanup = (async (): Promise<void> => {
    try {
      const r = await fetch(AUTH_WORKER + '/api/data/opportunities', { headers: authHeaders('GET'), cache: 'no-store' });
      if (!r.ok) return;
      const resp = await r.json() as { data?: { value?: unknown[] } | unknown[] };
      const dataObj = resp && resp.data;
      const raw = (dataObj && (Array.isArray(dataObj) ? dataObj : (dataObj as { value?: unknown[] }).value)) || [];
      const arr = Array.isArray(raw) ? raw : [];
      const cleaned = arr.filter((o) => !o || String((o as { id?: unknown }).id || '') !== String(holdOppId));
      if (cleaned.length === arr.length) return;
      await fetch(AUTH_WORKER + '/api/data/opportunities', { method: 'PUT', headers: authHeaders('PUT'), body: JSON.stringify({ value: cleaned }) });
    } catch { /* best-effort */ }
  })();

  const savePromise = saveFullShow(out);
  const pushPromise = newlyConfirmedUserIds.length > 0
    ? fetch(AUTH_WORKER + '/api/notify/show-confirmed', {
        method: 'POST',
        headers: authHeaders('POST'),
        body: JSON.stringify({
          showId: out.id, showName: out.name || '', showDate: out.date || '',
          showCity: out.city || '', recipientIds: newlyConfirmedUserIds,
        }),
      }).then(() => undefined).catch(() => undefined)
    : Promise.resolve();

  await Promise.all([savePromise, oppCleanup, pushPromise]);
  return out;
}

// ── "Logistics added" push (verbatim mirror of the saveFormToEvent travel diff) ─
function travelSig(t: TravelItem): string {
  if (!t || typeof t !== 'object') return '';
  return JSON.stringify({
    kind: t.type || '', number: t.confirmation || '',
    airline: t.airline || '', vendor: t.hotel || '',
    date: t.depDate || t.checkIn || '', whoFor: t.assignedTo || '',
  });
}
/** Fire /api/notify/logistics-added for travel rows added on an EDIT (not new). */
export async function notifyLogisticsAdded(ev: RawBookingEvent, prevTravel: ReadonlyArray<TravelItem>): Promise<void> {
  const prevSigs = (prevTravel || []).map(travelSig);
  const added = (ev.travel || []).filter((t) => { const s = travelSig(t); return s && prevSigs.indexOf(s) === -1; });
  if (added.length === 0) return;
  try {
    await fetch(AUTH_WORKER + '/api/notify/logistics-added', {
      method: 'POST',
      headers: authHeaders('POST'),
      body: JSON.stringify({
        showId: ev.id, showName: ev.name || ev.venue || '', showDate: ev.date || '',
        showCity: ev.city || '', addedCount: added.length,
        addedKinds: added.map((t) => String(t.type || 'item').toLowerCase()),
      }),
    });
  } catch { /* best-effort */ }
}

// ── Fetchers ─────────────────────────────────────────────────────────────────
function unwrapArr<T>(res: ScheduleResponse | ReadonlyArray<T> | null): T[] {
  if (Array.isArray(res)) return res.slice();
  if (res && typeof res === 'object') {
    const obj = res as ScheduleResponse;
    const data: unknown = obj.data;
    if (Array.isArray(data)) return (data as ReadonlyArray<T>).slice();
    if (data && typeof data === 'object') {
      const nested = (data as { value?: ReadonlyArray<T> }).value;
      if (Array.isArray(nested)) return nested.slice();
    }
    if (Array.isArray(obj.value)) return obj.value as T[];
  }
  return [];
}

/** GET /api/data/quotes → saved guarantee quotes (mirrors renderQuotes :1693). */
async function fetchQuotes(): Promise<BookingQuote[]> {
  const res = await apiGet<ScheduleResponse>('auth', '/api/data/quotes').catch(() => null);
  const rows = unwrapArr<Record<string, unknown>>(res);
  return rows.map((q, i) => {
    const venue = String(q.venue || q.name || 'Floor quote');
    const floor = q.floor != null ? '$' + Number(q.floor).toLocaleString('en-US') : '';
    const meta = [q.city, floor && 'floor ' + floor, q.date].filter(Boolean).join(' · ');
    return { id: String(q.id ?? i), venue, meta };
  });
}

// ── Editable quote (full shape, mirrors schedule-wiring.js openQuoteForm) ─────
export interface RawQuote {
  id: string;
  venue?: string;
  city?: string;
  date?: string;
  ask?: number;
  perPerson?: number;
  travel?: number;
  lodging?: number;
  perDiem?: number;
  backline?: number;
  agentPercent?: number;
  managerPercent?: number;
  notes?: string;
  updatedAt?: string;
}

/** GET /api/data/quotes → the full editable quote objects. */
export async function fetchRawQuotes(): Promise<RawQuote[]> {
  const res = await apiGet<ScheduleResponse>('auth', '/api/data/quotes').catch(() => null);
  const rows = unwrapArr<RawQuote>(res);
  return rows.map((q, i) => ({ ...q, id: String(q.id ?? 'q_' + i) }));
}

/** PUT the whole quotes array (verbatim port of schedule-wiring.js saveQuotes). */
export async function saveQuotes(quotes: readonly RawQuote[]): Promise<void> {
  try { await apiPut('auth', '/api/data/quotes', { value: quotes }); } catch { /* swallow */ }
}

/** Row subtitle for the quote list (city · date, else "Floor quote"). */
export function quoteMeta(q: RawQuote): string {
  return [q.city, q.date].filter(Boolean).join(' · ') || 'Floor quote';
}

export function newQuoteId(): string {
  return 'q_' + Date.now() + '_' + Math.random().toString(36).slice(2, 6);
}

// ════════════════════════════════════════════════════════════════════════════
// SETTLEMENT WORKSHEET, faithful 1:1 port of schedule-wiring.js readSettlement /
// computeSettlement / ensureSettlement + the settlement render-derived helpers.
// The settlement blob lives on the event row (event.settlement) inside the
// schedule blob; persistence is the same read-modify-write PUT /api/data/schedule
// that legacy saveSchedule() does (saveShow above).
// ════════════════════════════════════════════════════════════════════════════

/** Settlement expense category list (verbatim SETTLEMENT_EXPENSE_CATEGORIES). */
export const SETTLEMENT_EXPENSE_CATEGORIES: ReadonlyArray<{ id: string; label: string }> = [
  { id: 'sound', label: 'Sound' },
  { id: 'lights', label: 'Lights' },
  { id: 'stagehands', label: 'Stagehands' },
  { id: 'hospitality', label: 'Hospitality' },
  { id: 'marketing', label: 'Marketing' },
  { id: 'ticket_fees', label: 'Ticket fees' },
  { id: 'cc_fees', label: 'CC fees' },
  { id: 'other', label: 'Other' },
];

/** Normalized settlement read-model (verbatim readSettlement defaults). */
export interface ReadSettlement {
  status: 'pending' | 'in_progress' | 'settled';
  door: Required<Pick<SettlementDoor, 'presale_sold' | 'presale_price' | 'walkup_sold' | 'walkup_price' | 'vip_sold' | 'vip_price' | 'comps_in' | 'ga_sold' | 'ga_price' | 'walkup_pct'>>;
  expenses: SettlementExpense[];
  show_expenses: SettlementExpense[];
  splits: {
    guarantee: number;
    deposit: number;
    door_split_pct: number;
    door_split_after: number;
    tax_withholding_pct: number;
    settled_split_base: number;
    bonuses: Array<{ label?: string; amount?: number }>;
    payouts: SettlementPayout[];
  };
  merch_cut: {
    enabled: boolean;
    percentage: number;
    percentage_recorded: number;
    sales_amount: number;
    sales_source: string;
    cut_owed: number;
    last_pulled_at: string | null;
  };
  payments_received: SettlementPayment[];
  net_to_artist: number;
  settled_at: string | null;
  updated_at: string | null;
}

/** Computed settlement totals (verbatim computeSettlement return shape). */
export interface ComputedSettlement {
  gross_presale: number;
  gross_walkup: number;
  gross_ga: number;
  gross_vip: number;
  gross_door: number;
  total_exp: number;
  showExp: number;
  bandExp: number;
  net_door: number;
  door_share: number;
  bonus_total: number;
  merch_sales: number;
  merch_owed: number;
  merch_net: number;
  payouts_total: number;
  tax_withheld: number;
  vip_cohead_share: number;
  net_to_artist: number;
  take_home: number;
  paid_via: 'door' | 'guarantee';
}

const num = (v: unknown): number => Number(v) || 0;

/** Verbatim port of schedule-wiring.js readSettlement(ev). */
/** Representative door price from the ticket tiers (the priced tier with the
 *  largest cap). Lets the contract's scaling "talk to" the settlement — the
 *  door PRE $ / WALK $ default to the tier price instead of $0. (Ryan 2026-07-22) */
function _primaryTierPrice(ev: RawBookingEvent | undefined | null): number {
  const tiers = (ev && Array.isArray(ev.ticketTiers)) ? ev.ticketTiers : [];
  let best: { price: number; cap: number } | null = null;
  for (const t of tiers) {
    const price = Number(t && t.price) || 0;
    if (price <= 0) continue;
    const cap = Number(t && t.cap) || 0;
    if (!best || cap > best.cap) best = { price, cap };
  }
  return best ? best.price : 0;
}

export function readSettlement(ev: RawBookingEvent | undefined | null): ReadSettlement {
  const s = (ev && ev.settlement) || {};
  const door = s.door || {};
  const splits = s.splits || {};
  const mc = s.merch_cut || {};
  const presaleSold = (door.presale_sold != null) ? num(door.presale_sold) : num(door.ga_sold);
  const tierPrice = _primaryTierPrice(ev);
  const presalePrice = (door.presale_price != null) ? num(door.presale_price)
    : (door.ga_price != null) ? num(door.ga_price)
    : tierPrice;
  return {
    status: (s.status as ReadSettlement['status']) || 'pending',
    door: {
      presale_sold: presaleSold || 0,
      presale_price: presalePrice || 0,
      walkup_sold: num(door.walkup_sold) || 0,
      walkup_price: (door.walkup_price != null) ? (num(door.walkup_price) || 0) : tierPrice,
      vip_sold: num(door.vip_sold) || 0,
      vip_price: num(door.vip_price) || 0,
      comps_in: num(door.comps_in) || 0,
      ga_sold: presaleSold + (num(door.walkup_sold) || 0),
      ga_price: presalePrice || 0,
      walkup_pct: num(door.walkup_pct) || 0,
    },
    expenses: Array.isArray(s.expenses) ? s.expenses.slice() : [],
    show_expenses: Array.isArray(s.show_expenses) ? s.show_expenses.slice() : [],
    splits: {
      guarantee: num(splits.guarantee) || 0,
      deposit: num(splits.deposit) || 0,
      door_split_pct: num(splits.door_split_pct) || 0,
      door_split_after: num(splits.door_split_after) || 0,
      tax_withholding_pct: num(splits.tax_withholding_pct) || 0,
      settled_split_base: num(splits.settled_split_base) || 0,
      bonuses: Array.isArray(splits.bonuses) ? splits.bonuses.slice() : [],
      payouts: Array.isArray(splits.payouts) ? splits.payouts.slice() : [],
    },
    merch_cut: {
      enabled: !!mc.enabled,
      percentage: num(mc.percentage) || 0,
      percentage_recorded: num(mc.percentage_recorded) || 0,
      sales_amount: num(mc.sales_amount) || 0,
      sales_source: mc.sales_source || 'manual',
      cut_owed: num(mc.cut_owed) || 0,
      last_pulled_at: mc.last_pulled_at || null,
    },
    net_to_artist: num(s.net_to_artist) || 0,
    payments_received: Array.isArray(s.payments_received) ? (s.payments_received as SettlementPayment[]).slice() : [],
    settled_at: s.settled_at || null,
    updated_at: s.updated_at || null,
  };
}

/** Verbatim port of schedule-wiring.js computeSettlement(s, ev). */
export function computeSettlement(s: ReadSettlement, ev?: RawBookingEvent): ComputedSettlement {
  const d = s.door || ({} as ReadSettlement['door']);
  const gross_presale = (d.presale_sold || 0) * (d.presale_price || 0);
  const gross_walkup = (d.walkup_sold || 0) * (d.walkup_price || 0);
  const gross_ga = gross_presale + gross_walkup;
  const gross_vip = (d.vip_sold || 0) * (d.vip_price || 0);
  const gross_door = gross_ga + gross_vip;
  const bookingExp = (ev && ev.financials && ev.financials.otherExpenses)
    ? ev.financials.otherExpenses.reduce((a, x) => a + (num(x && x.amount)), 0)
    : 0;
  const fb = (ev && ev.financials) || {};
  const memberCount = Math.max(1, num(fb.memberCount) || (ev && ev.memberContacts && ev.memberContacts.length) || 1);
  const stdBookingExp =
    num(fb.hotel) +
    num(fb.flights) +
    num(fb.gas) +
    num(fb.rental) +
    num(fb.photoVideo) +
    num(fb.foh) +
    (num(fb.perDiem) * memberCount);
  /* Show/venue expenses (the settlement worksheet's itemized costs: rent,
     staffing, production…) reduce the SPLIT POOL that the door % is taken
     from — they are NOT the band's costs and must not come out of the
     artist's take. Band touring costs (hotel/flights/per-diem, from the
     booking form) are what reduce take-home. Conflating the two produced a
     fake loss on guarantee deals (Ryan 2026-07-22). */
  const showExp = (s.show_expenses || []).reduce((a, e) => a + num(e.amount), 0) + (s.expenses || []).reduce((a, e) => a + num(e.amount), 0);
  const bandExp = bookingExp + stdBookingExp;
  const total_exp = showExp + bandExp;
  const net_door = gross_door - showExp;
  const splits = s.splits || ({} as ReadSettlement['splits']);
  const guarantee = num(splits.guarantee);
  const door_pct = num(splits.door_split_pct);
  const threshold = num(splits.door_split_after);
  const coVip = !!(ev && ev.coVipSplit) && gross_vip > 0;
  let door_share = 0;
  if (door_pct > 0) {
    /* If Prism already gave us the settled pool (net revenue to split), the % is
       taken on THAT — we don't re-derive it from gross minus expenses (the artist
       side doesn't hold the venue's expenses). Else fall back to net_door. */
    /* Settled-show split base, in priority order:
       1) settled_split_base (Prism's net-revenue-to-split, stored on import)
       2) reference.net_revenue_to_split (same figure, hidden reference block —
          survives even if a stale record never stamped settled_split_base)
       3) net_door (manual shows only). Never take the % on the FULL gross: a
          settled artist holds no venue expenses, so net_door == gross there and
          the door share would balloon past the guarantee. (Ryan 2026-07-22) */
    const refNetRev = num(ev && ev.settlement && ev.settlement.reference && ev.settlement.reference.net_revenue_to_split);
    const splitBase0 = num(splits.settled_split_base) > 0 ? num(splits.settled_split_base)
      : (refNetRev > 0 ? refNetRev : net_door);
    /* Co-headline VIP: VIP gross is not part of the split pool — the two
       co-headliners split it 50/50 separately (Ryan 2026-07-22). */
    const splitBase = coVip ? Math.max(0, splitBase0 - gross_vip) : splitBase0;
    const splittable = Math.max(0, splitBase - threshold);
    door_share = splittable * (door_pct / 100);
  }
  const bonus_total = (splits.bonuses || []).reduce((a, b) => a + num(b.amount), 0);
  const mc = s.merch_cut || ({} as ReadSettlement['merch_cut']);
  const mc_sales = num(mc.sales_amount);
  const mc_pct = num(mc.percentage);
  const mc_owed = mc.enabled ? (mc_sales * mc_pct / 100) : 0;
  const headline_take = Math.max(guarantee, door_share);
  const payouts_total = (splits.payouts || []).reduce((a, p) => {
    const amt = num(p && p.amount);
    if (p && p.kind === 'percent') return a + (headline_take * amt / 100);
    return a + amt;
  }, 0);
  const tax_withheld = Math.max(0, headline_take) * (num(splits.tax_withholding_pct) / 100);
  /* Co-headline VIP share: this artist keeps 50% of VIP gross, credited
     directly (post-tax; the venue withholding is on the guarantee/door only). */
  const vip_cohead_share = coVip ? Math.round(gross_vip * 0.5 * 100) / 100 : 0;
  const net_to_artist = headline_take + bonus_total - mc_owed - payouts_total - tax_withheld + vip_cohead_share;
  const take_home = net_to_artist - bandExp;
  const merch_net = mc.enabled ? (mc_sales - mc_owed) : mc_sales;
  return {
    gross_presale, gross_walkup, gross_ga, gross_vip, gross_door,
    total_exp, showExp, bandExp, net_door, door_share, bonus_total,
    merch_sales: mc_sales, merch_owed: mc_owed, merch_net,
    payouts_total, tax_withheld, vip_cohead_share, net_to_artist, take_home,
    paid_via: (door_share > guarantee) ? 'door' : 'guarantee',
  };
}

/** Verbatim port of ensureSettlement, lazy-creates the blob + seeds defaults.
 *  Mutates `ev.settlement` in place (the caller persists via saveShow). */
export function ensureSettlement(ev: RawBookingEvent): SettlementBlob {
  const evt = ev as RawBookingEvent & { settlement?: SettlementBlob };
  if (!evt.settlement) evt.settlement = {};
  const st = evt.settlement;
  if (!st.door) st.door = {};
  if (!st.expenses) st.expenses = [];
  if (!st.splits) st.splits = {};
  if (!st.merch_cut) st.merch_cut = { enabled: false, percentage: 0, sales_amount: 0, sales_source: 'manual', cut_owed: 0 };
  if (!st.status) st.status = 'pending';
  const f = ev.financials || {};
  const splits = st.splits as SettlementSplits;
  if (!splits.guarantee && f.totalPay) splits.guarantee = num(f.totalPay);
  if (f.doorSplit && !splits.door_split_pct) splits.door_split_pct = 100;
  return st;
}

/** Verbatim port of settlementStatusLabel. */
export function settlementStatusLabel(status: string | undefined): string {
  if (status === 'in_progress') return 'In Progress';
  if (status === 'settled') return 'Settled';
  return 'Pending';
}

// ── Receivables (Outstanding card) ────────────────────────────────────────────
// Total OWED to the artist by the promoter (a receivable, not net-after-payouts):
// a door deal pays the artist's door share; otherwise the booked guarantee.
export function settlementTotalOwed(ev: RawBookingEvent): number {
  const s = readSettlement(ev);
  const hasWorksheet = !!(ev.settlement && (ev.settlement as { door?: unknown }).door);
  if (hasWorksheet) {
    const c = computeSettlement(s, ev);
    if (c.paid_via === 'door') return Math.round(c.door_share) || 0;
  }
  return Number(ev.financials?.totalPay) || s.splits.guarantee || 0;
}
/** Money in so far = deposit (on-signing) + every logged payment. */
export function settlementReceived(ev: RawBookingEvent): number {
  const s = readSettlement(ev);
  const pays = s.payments_received.reduce((a, p) => a + (Number(p.amount) || 0), 0);
  return (s.splits.deposit || 0) + pays;
}
/** Append a received payment to a show and persist (band-scoped via authHeaders'
 *  X-Band-Id, same as saveShow). Auto-marks the settlement settled once the
 *  received total covers what's owed. Returns false if the show isn't found. */
export async function logSettlementPayment(
  showId: string,
  payment: { amount: number; date: string; note?: string },
): Promise<boolean> {
  const res = await apiGet<ScheduleResponse>('auth', '/api/data/schedule').catch(() => null);
  const arr = unwrapArr<RawBookingEvent>(res);
  if (!Array.isArray(arr)) return false;
  const ev = arr.find((e) => String(e.id) === String(showId));
  if (!ev) return false;
  const st = (ev.settlement || {}) as SettlementBlob;
  const pays: SettlementPayment[] = Array.isArray(st.payments_received) ? st.payments_received.slice() : [];
  pays.push({ id: 'pay_' + Date.now(), date: payment.date, amount: Number(payment.amount) || 0, note: payment.note });
  st.payments_received = pays;
  const received = (Number(st.splits?.deposit) || 0) + pays.reduce((a, p) => a + (Number(p.amount) || 0), 0);
  const total = settlementTotalOwed(ev);
  if (total > 0 && received >= total) { st.status = 'settled'; st.settled_at = new Date().toISOString(); }
  else if (received > 0) { st.status = 'in_progress'; }
  (ev as { settlement?: unknown }).settlement = st;
  try { await apiPut('auth', '/api/data/schedule', { value: arr }); } catch { return false; }
  return true;
}

/** Verbatim port of fmtMoney (2 decimals, leading $, sign-aware). */
export function fmtMoney(n: number): string {
  const v = num(n);
  const sign = v < 0 ? '-' : '';
  const abs = Math.abs(v);
  return sign + '$' + abs.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/** Full USD with thousands separators (mirror fmtUsdFull from schedule-wiring.js). */
export function fmtUsdFull(n: number): string {
  return '$' + num(n).toLocaleString('en-US');
}

/** Editable-settlement role gate (verbatim canEditSettlement): full | no_lifetime. */
export function canEditSettlement(): boolean {
  const perms = (typeof document !== 'undefined' && document.body && document.body.getAttribute('data-perms-financials')) || 'full';
  return perms === 'full' || perms === 'no_lifetime';
}

// ── Comp count (guest list), verbatim loadSettleGuestCount fetch ─────────────
/** GET /api/shows/<id>/guests → comp seats (sum of 1 + plus_count per row). */
export async function fetchGuestCompCount(showId: string): Promise<number> {
  try {
    const r = await fetch(AUTH_WORKER + '/api/shows/' + encodeURIComponent(showId) + '/guests', { headers: authHeaders('GET'), cache: 'no-store' });
    if (!r.ok) return 0;
    const data = await r.json() as { guests?: Array<{ plus_count?: number }> };
    const guests = data.guests || [];
    return guests.reduce((a, g) => a + 1 + (num(g.plus_count)), 0);
  } catch { return 0; }
}

// ── POS merch pull, verbatim settle-merch-pull (Shopify POS-only → Square) ────
export interface MerchPullResult { salesToday: number; source: string }
/** Probe Shopify summary (POS-only), fall back to Square. Null when neither. */
export async function pullMerchSales(): Promise<MerchPullResult | null> {
  const tz = (Intl.DateTimeFormat().resolvedOptions() || {}).timeZone || 'America/Chicago';
  const tryEndpoint = async (path: string, source: string, posOnly: boolean): Promise<{ ok: boolean; j: { error?: string; salesToday?: number }; source: string }> => {
    const r = await fetch(AUTH_WORKER + path, {
      method: 'POST',
      headers: authHeaders('POST'),
      body: JSON.stringify({ tz, pos_only: posOnly }),
    });
    const j = await r.json() as { error?: string; salesToday?: number };
    return { ok: r.ok, j, source };
  };
  try {
    let res = await tryEndpoint('/api/proxy/shopify-summary', 'shopify', true);
    if (!(res.ok && res.j && !res.j.error && num(res.j.salesToday) >= 0)) {
      res = await tryEndpoint('/api/proxy/square-summary', 'square', false);
    }
    if (!res.ok || !res.j || res.j.error) return null;
    return { salesToday: num(res.j.salesToday), source: res.source };
  } catch { return null; }
}

// ── Friends + invite statuses (lineup chip "attached/pending/invite" states) ──
export interface FriendRow {
  id?: string | number;
  status?: string;
  from_band?: string;
  to_band?: string;
  from_name?: string;
  to_name?: string;
  band_name?: string;
  name?: string;
}
export interface InviteStatusRow { status?: string; date?: string; friendBand?: string }

/** GET /api/friends → accepted friend rows (mirror loadFriends filter). */
export async function fetchAcceptedFriends(): Promise<FriendRow[]> {
  try {
    const r = await fetch(AUTH_WORKER + '/api/friends', { headers: authHeaders('GET'), cache: 'no-store' });
    if (!r.ok) return [];
    const fr = await r.json() as { friends?: FriendRow[] };
    return (fr.friends || []).filter((f) => f.status === 'accepted');
  } catch { return []; }
}

/** GET /api/friends/invite-statuses → sharedDateId → {status,date,friendBand}. */
export async function fetchInviteStatuses(): Promise<Record<string, InviteStatusRow>> {
  try {
    const r = await fetch(AUTH_WORKER + '/api/friends/invite-statuses', { headers: authHeaders('GET'), cache: 'no-store' });
    if (!r.ok) return {};
    const res = await r.json() as { success?: boolean; statuses?: Record<string, InviteStatusRow> };
    return (res && res.success && res.statuses) ? res.statuses : {};
  } catch { return {}; }
}

/** POST /api/friends/<id>/share-dates, invite a friend band to a show (verbatim). */
export async function inviteFriendToShow(friendId: string, ev: RawBookingEvent): Promise<boolean> {
  const lineupArr = Array.isArray(ev.lineup) ? ev.lineup : [];
  const payload = {
    date: ev.date || '',
    name: ev.name || '',
    venue: ev.name || '',
    city: ev.city || '',
    type: 'show',
    showCategory: ev.showCategory || 'show',
    venueAddress: ev.venueAddress || '',
    lineup: lineupArr.map((a) => (typeof a === 'string' ? { name: a } : { name: a.name, role: a.role, bit_id: a.bit_id || null, bit_img: a.bit_img || null })),
    ticketLink: ev.ticketLink || '',
    dayOfShow: ev.dayOfShow || {},
  };
  try {
    const r = await fetch(AUTH_WORKER + '/api/friends/' + encodeURIComponent(friendId) + '/share-dates', {
      method: 'POST',
      headers: authHeaders('POST'),
      body: JSON.stringify({ dates: [payload] }),
    });
    return r.ok;
  } catch { return false; }
}

// ── Shared-show collaborative sync (Option A, Ryan 2026-07-21) ─────────────────
// Once bands are connected on a show, Basics + Lineup + Run of Show live in ONE
// shared record (shared_dates.event_data); every connected band reads + writes it
// (last-write-wins). Private fields (money/travel/members/vip/contract) stay in
// each band's own copy. These helpers resolve the shared id and read/write it.
export interface ShareLink { id: string; role: 'owner' | 'recipient'; friendBand: string }
export interface SharedCollab {
  basics?: Partial<RawBookingEvent>;
  lineup?: LineupAct[];
  dayOfShow?: Record<string, unknown>;
}

/** GET /api/friends/shared-link — the sharedDateId linking us to a show on `date`
 *  (works whether we're the host or a support). null when the show isn't shared. */
export async function resolveShareLink(date: string, name?: string): Promise<{ sharedDateId: string | null; links: ShareLink[] }> {
  try {
    if (!date) return { sharedDateId: null, links: [] };
    const qs = 'date=' + encodeURIComponent(date) + (name ? '&name=' + encodeURIComponent(name) : '');
    const r = await fetch(AUTH_WORKER + '/api/friends/shared-link?' + qs, { headers: authHeaders('GET') });
    const j = await r.json() as { ok?: boolean; sharedDateId?: string | null; links?: ShareLink[] };
    if (!r.ok || !j.ok) return { sharedDateId: null, links: [] };
    return { sharedDateId: j.sharedDateId || null, links: Array.isArray(j.links) ? j.links : [] };
  } catch { return { sharedDateId: null, links: [] }; }
}

/** GET the shared record's collaborative fields + who/when last changed them. */
export async function fetchSharedCollab(sharedDateId: string): Promise<{ basics: Partial<RawBookingEvent>; lineup: LineupAct[]; dayOfShow: Record<string, unknown>; updatedAt: string | null; updatedBy: string | null } | null> {
  try {
    const r = await fetch(AUTH_WORKER + '/api/friends/shared-dates/' + encodeURIComponent(sharedDateId) + '/collab', { headers: authHeaders('GET') });
    const j = await r.json() as { ok?: boolean; collab?: { basics?: Partial<RawBookingEvent>; lineup?: LineupAct[]; dayOfShow?: Record<string, unknown> }; updatedAt?: string | null; updatedBy?: string | null };
    if (!r.ok || !j.ok || !j.collab) return null;
    return {
      basics: j.collab.basics || {},
      lineup: Array.isArray(j.collab.lineup) ? j.collab.lineup : [],
      dayOfShow: j.collab.dayOfShow || {},
      updatedAt: j.updatedAt || null,
      updatedBy: j.updatedBy || null,
    };
  } catch { return null; }
}

/** POST the shared collaborative fields (last-write-wins). */
export async function pushSharedCollab(sharedDateId: string, collab: SharedCollab): Promise<boolean> {
  try {
    const r = await fetch(AUTH_WORKER + '/api/friends/shared-dates/' + encodeURIComponent(sharedDateId) + '/collab', {
      method: 'POST', headers: authHeaders('POST'), body: JSON.stringify({ collab }),
    });
    return r.ok;
  } catch { return false; }
}

const _SHARED_BASIC_KEYS: ReadonlyArray<keyof RawBookingEvent> = ['name','date','venue','city','venueAddress','promoter','capacity','ticketTiers','ageRestriction','ticketLink','tourName','showCategory','flyer','flyerUrl','flyerKey','flyerName'];

/** Pull the shared (collaborative) fields out of a show, for pushing to the record. */
export function extractSharedCollab(ev: RawBookingEvent): SharedCollab {
  const basics: Partial<RawBookingEvent> = {};
  _SHARED_BASIC_KEYS.forEach((k) => { if (ev[k] !== undefined) (basics as Record<string, unknown>)[k] = ev[k]; });
  return {
    basics,
    lineup: Array.isArray(ev.lineup) ? (ev.lineup.filter((a): a is LineupAct => typeof a !== 'string')) : [],
    dayOfShow: (ev.dayOfShow || {}) as Record<string, unknown>,
  };
}

/** Merge the shared collab fields INTO a show — shared wins for Basics/Lineup/RoS,
 *  the band's private fields (money/travel/members/…) are left untouched. */
export function mergeSharedCollab(ev: RawBookingEvent, collab: { basics?: Partial<RawBookingEvent>; lineup?: LineupAct[]; dayOfShow?: Record<string, unknown> }): RawBookingEvent {
  const next: RawBookingEvent = { ...ev };
  if (collab.basics && typeof collab.basics === 'object') {
    Object.keys(collab.basics).forEach((k) => { (next as Record<string, unknown>)[k] = (collab.basics as Record<string, unknown>)[k]; });
  }
  if (Array.isArray(collab.lineup) && collab.lineup.length) next.lineup = collab.lineup;
  if (collab.dayOfShow && typeof collab.dayOfShow === 'object') next.dayOfShow = { ...(ev.dayOfShow || {}), ...(collab.dayOfShow as object) };
  return next;
}

/** GET all shared collab records for the band, keyed by date. The Booking list
 *  merges the `basics` in so shared fields (tour name → tour folders, venue,
 *  capacity) render at LIST level without opening each show (Ryan 2026-07-22). */
export async function fetchAllSharedCollab(): Promise<Record<string, { basics: Partial<RawBookingEvent>; lineup: LineupAct[]; dayOfShow: Record<string, unknown>; updatedAt: string | null }>> {
  try {
    const r = await fetch(AUTH_WORKER + '/api/friends/shared-collab-all', { headers: authHeaders('GET') });
    const j = await r.json() as { ok?: boolean; byDate?: Record<string, { basics?: Partial<RawBookingEvent>; lineup?: LineupAct[]; dayOfShow?: Record<string, unknown>; updatedAt?: string | null }> };
    if (!r.ok || !j.ok || !j.byDate) return {};
    const out: Record<string, { basics: Partial<RawBookingEvent>; lineup: LineupAct[]; dayOfShow: Record<string, unknown>; updatedAt: string | null }> = {};
    Object.keys(j.byDate).forEach((d) => {
      const c = j.byDate![d] || {};
      out[d] = { basics: c.basics || {}, lineup: Array.isArray(c.lineup) ? c.lineup : [], dayOfShow: c.dayOfShow || {}, updatedAt: c.updatedAt || null };
    });
    return out;
  } catch { return {}; }
}

// ── Venue-contributed deal data (VC→AC bridge). The venue writes tickets sold /
//    attendance into the shared deal; the artist reads it here so a linked
//    show reflects the venue's numbers live. (Ryan 2026-07-22) ──
export interface VenueDeal {
  deal_id: string;
  venue_name: string | null;
  venue_city: string | null;
  date: string;
  venue: { tickets_sold?: number; attendance?: number; capacity?: number; [k: string]: unknown };
  updated_at: string | null;
}
export async function fetchVenueDeals(date: string): Promise<VenueDeal[]> {
  try {
    if (!date) return [];
    const r = await fetch(AUTH_WORKER + '/api/venue-deals?date=' + encodeURIComponent(String(date).slice(0, 10)), { headers: authHeaders('GET') });
    const j = await r.json() as { ok?: boolean; deals?: VenueDeal[] };
    if (!r.ok || !j.ok || !Array.isArray(j.deals)) return [];
    return j.deals;
  } catch { return []; }
}

// ── Invite members to a hold/show, verbatim inviteMembersToHold flow ──────────
export interface InviteMembersResult { ok: boolean; message: string }
/** Build the show_hold opp, write it into the band-shared opportunities bucket,
 *  then fan out /api/opportunity/invite (verbatim _inviteMembersToHold_send).
 *  Returns the set of names stamped pending so the caller can update the show. */
export async function inviteMembersToHold(ev: RawBookingEvent, user: User | null): Promise<InviteMembersResult & { pendingNames: string[]; invitees: Array<{ name: string; userId: string; email: string }> }> {
  const allMembers = (ev.memberContacts || []).filter((m) => m && (m.userId || m.email || (m.firstName || m.lastName)));
  if (allMembers.length === 0) {
    return { ok: false, message: 'Add band members to this show before inviting them. Use the Members section in the Edit form.', pendingNames: [], invitees: [] };
  }
  const responses = ev.responses || {};
  const CONFIRMED = new Set(['yes', 'in']);
  const statusFor = (m: MemberContact): string => {
    const nm = ((m.firstName || '') + ' ' + (m.lastName || '')).trim().toLowerCase();
    if (!nm) return 'pending';
    const keys = Object.keys(responses);
    for (const k of keys) if (k.toLowerCase() === nm) return responses[k];
    return 'pending';
  };
  const members = allMembers.filter((m) => !CONFIRMED.has(String(statusFor(m) || '').toLowerCase()));
  if (members.length === 0) {
    return { ok: false, message: 'All members already confirmed for this date, nothing to send.', pendingNames: [], invitees: [] };
  }
  const u = user || ({} as User);
  const invitees = members.map((m) => ({
    user_id: (m.userId != null ? String(m.userId) : '') || '',
    email: m.email || '',
    bandName: '',
    name: ((m.firstName || '') + ' ' + (m.lastName || '')).trim() || m.email || 'Member',
  }));
  const opp = {
    id: 'show_hold:' + ev.id,
    type: 'show_hold',
    confirmed: !!ev.confirmed,
    title: ev.name || 'Show',
    date: ev.date,
    time: (ev.dayOfShow && ev.dayOfShow.doors) || '',
    address: ev.venueAddress || ((ev.name || '') + (ev.city ? (' · ' + ev.city) : '')),
    city: ev.city || '',
    showId: ev.id,
    createdById: (u as { id?: string | number }).id || '',
    createdByName: (u as { display_name?: string; first_name?: string }).display_name || (u as { first_name?: string }).first_name || '',
    createdByBand: u.band_name || '',
    createdByAvatar: (u as { avatar_url?: string }).avatar_url || '',
    _inviterName: (u as { display_name?: string; first_name?: string }).display_name || (u as { first_name?: string }).first_name || '',
    invitees: invitees.map((inv) => ({ name: inv.name, userId: inv.user_id || '', email: inv.email || '' })),
    responses: invitees.reduce((acc: Record<string, string>, inv) => {
      const key = inv.user_id || inv.email || inv.name;
      if (key) acc[key] = 'pending';
      return acc;
    }, {}),
  };
  try {
    // STEP 1: merge the opp into the band-shared opportunities bucket.
    const rg = await fetch(AUTH_WORKER + '/api/data/opportunities', { headers: authHeaders('GET'), cache: 'no-store' });
    const resp = rg.ok ? await rg.json() as { data?: { value?: unknown[] } | unknown[] } : { data: [] };
    const dataObj = resp && resp.data;
    const raw = (dataObj && (Array.isArray(dataObj) ? dataObj : (dataObj as { value?: unknown[] }).value)) || [];
    let arr = Array.isArray(raw) ? raw.slice() : [];
    arr = arr.filter((o) => !o || String((o as { id?: unknown }).id) !== String(opp.id));
    arr.push(opp);
    await fetch(AUTH_WORKER + '/api/data/opportunities', { method: 'PUT', headers: authHeaders('PUT'), body: JSON.stringify({ value: arr }) });
    // STEP 2: fan out the invite (push + cross-band invites_received).
    const ri = await fetch(AUTH_WORKER + '/api/opportunity/invite', {
      method: 'POST',
      headers: authHeaders('POST'),
      body: JSON.stringify({ opp, invitees, pushKind: 'show_hold_invite' }),
    });
    const j = await ri.json() as { ok?: boolean; success?: boolean; error?: string; message?: string };
    if (ri.ok && (j.ok || j.success)) {
      return { ok: true, message: 'Sent, members will see this in Opportunities and get a push.', pendingNames: invitees.map((i) => i.name), invitees: invitees.map((inv) => ({ name: inv.name, userId: inv.user_id || '', email: inv.email || '' })) };
    }
    return { ok: false, message: 'Invite failed: ' + ((j && (j.error || j.message)) || ('HTTP ' + ri.status)), pendingNames: [], invitees: [] };
  } catch {
    return { ok: false, message: 'Network error sending invite.', pendingNames: [], invitees: [] };
  }
}

// ════════════════════════════════════════════════════════════════════════════
// AVATAR BACKFILL, faithful port of schedule-wiring.js hydrateLineupAvatars /
// hydrateVenueAvatar. When a lineup act (or the venue) has no stored bit_img/
// image, query the Bandsintown artist/venue/festival autocomplete by name and
// take the first match's image. Cached per-name within the session so repeated
// re-renders don't refetch (legacy __actImgCache / __venueImgCache). A cached
// `false` is a known-miss → render initials and never refetch.
// ════════════════════════════════════════════════════════════════════════════

/** mediaId → BIT thumb URL (verbatim legacy convention). */
function bitThumbUrl(hit: { mediaId?: string; imageUrl?: string } | null | undefined): string | null {
  if (!hit) return null;
  if (hit.mediaId) return 'https://photos.bandsintown.com/thumb/' + hit.mediaId + '.jpeg';
  return hit.imageUrl || null;
}

// Session caches (string URL = hit, false = known miss, undefined = unfetched).
const __actImgCache: Record<string, string | false> = {};

/** Look up a lineup act's avatar by name. Verbatim hydrateLineupAvatars match
 *  priority: exact-name → first verified → first. Caches by lowercased name. */
export async function backfillArtistImage(name: string): Promise<string | null> {
  const key = String(name || '').trim().toLowerCase();
  if (!key) return null;
  if (__actImgCache[key] === false) return null;
  if (__actImgCache[key]) return __actImgCache[key] as string;
  try {
    const r = await fetch(AUTH_WORKER + '/api/bandsintown/artist-autocomplete?query=' + encodeURIComponent(name), { headers: authHeaders('GET'), cache: 'no-store' });
    const data = r.ok ? await r.json() as { success?: boolean; artists?: Array<{ name?: string; verified?: boolean; mediaId?: string; imageUrl?: string }> } : null;
    if (!data || !data.success || !data.artists || !data.artists.length) { __actImgCache[key] = false; return null; }
    const exact = data.artists.find((a) => String(a.name || '').toLowerCase() === key);
    const pick = exact || data.artists.find((a) => a.verified) || data.artists[0];
    const url = bitThumbUrl(pick);
    __actImgCache[key] = url || false;
    return url;
  } catch { __actImgCache[key] = false; return null; }
}

// dead-code 2026-07-06: backfillVenueImage() removed, it was exported but
// called nowhere (venue thumbnails are hydrated inline where needed).

// ════════════════════════════════════════════════════════════════════════════
// BANDSINTOWN PUSH, faithful port of schedule-wiring.js pushToBandsintown /
// renderBitConfirmation / confirmBitPush. The legacy flow is NOT a one-shot
// POST: it (1) validates, (2) resolves the venue/festival + every lineup act in
// parallel via the *-autocomplete endpoints, (3) shows a confirmation modal with
// verification badges, then (4) on confirm posts the FULL push-event payload
// (event_type concert|festival, venue/festival/edition ids, draft flag).
// ════════════════════════════════════════════════════════════════════════════

export interface BitPushResult { ok: boolean; message: string; bitUrl?: string; bitEventId?: string }

/** TESTING flag (verbatim BIT_PUSH_AS_DRAFT), route pushes to BIT Drafts. */
export const BIT_PUSH_AS_DRAFT = true;

/** Resolved venue/festival entity for the confirmation interstitial. */
export interface BitResolvedVenue {
  name: string;
  bit_id?: string | number | null;
  address?: string;
  city?: string;
  region?: string;
  country?: string;
  latitude?: number | null;
  longitude?: number | null;
  verified?: boolean;
  trackerCount?: number;
  isFestival?: boolean;
  fromAddress?: boolean;
}
/** Resolved lineup act for the confirmation interstitial. */
export interface BitResolvedArtist {
  local_name: string;
  bit_name: string;
  bit_id?: string | number | null;
  image?: string;
  followers?: number;
  role: string;
  isYou: boolean;
  verified: boolean;
}
export interface BitResolution {
  venue: BitResolvedVenue | null;
  artists: BitResolvedArtist[];
  isFest: boolean;
  artistName: string;
}
/** Validation outcome, `error` non-null means the push can't proceed. */
export interface BitPushPrecheck { ok: boolean; error?: string; artistName?: string }

/** Read the linked Bandsintown connection (legacy window.ac.connections). */
async function bitConnectionName(): Promise<string | null> {
  try {
    const { fetchConnBlob } = await import('./settings');
    const blob = await fetchConnBlob();
    const bit = (blob.bandsintown || {}) as { id?: string; artist?: string };
    const name = bit.id || bit.artist;
    return name ? String(name) : null;
  } catch { return null; }
}

/** Verbatim port of the pushToBandsintown validation gate. */
export async function precheckBitPush(ev: RawBookingEvent): Promise<BitPushPrecheck> {
  if ((ev as { isOffDay?: boolean }).isOffDay || ev.type === 'off') {
    return { ok: false, error: 'Off / travel days never push to Bandsintown.' };
  }
  if (ev.type && ev.type !== 'show') {
    return { ok: false, error: 'Only show / festival dates can push to Bandsintown.' };
  }
  if (!ev.confirmed) return { ok: false, error: 'Confirm the date before pushing to Bandsintown.' };
  const artistName = await bitConnectionName();
  if (!artistName) return { ok: false, error: 'Connect Bandsintown in Settings → Connections first.' };
  return { ok: true, artistName };
}

/** Resolve the venue/festival + every lineup act (parallel autocomplete lookups).
 *  Verbatim port of pushToBandsintown's resolution phase. */
export async function resolveBitPush(ev: RawBookingEvent, artistName: string): Promise<BitResolution> {
  const isFest = ev.showCategory === 'fest';

  // Venue city/state fallback parse out of venueAddress (verbatim).
  let venueCity = '';
  let venueRegion = '';
  const pick = (ev as { bitVenuePick?: Record<string, unknown> | null }).bitVenuePick as Record<string, unknown> | null | undefined;
  if (ev.venueAddress) {
    const parts = ev.venueAddress.split(',').map((s) => s.trim());
    if (parts.length >= 2) {
      venueCity = parts[parts.length - 2] || '';
      const lastPart = parts[parts.length - 1] || '';
      const stateMatch = lastPart.match(/^([A-Z]{2})(\s|$)/);
      venueRegion = stateMatch ? stateMatch[1] : lastPart;
    } else if (parts.length === 1) {
      venueCity = parts[0];
    }
  } else if (ev.city) {
    venueCity = ev.city;
  }
  let venue: BitResolvedVenue | null = { name: ev.name || 'TBA', city: venueCity, region: venueRegion, fromAddress: true };

  // Lineup, "you" first as headliner (verbatim).
  const allActs: Array<{ name: string; role: string; isYou: boolean; bit_id?: string | number | null }> =
    [{ name: artistName, role: 'headliner', isYou: true }];
  if (Array.isArray(ev.lineup) && ev.lineup.length) {
    ev.lineup.forEach((act) => {
      if (typeof act === 'string') { if (act && act !== artistName) allActs.push({ name: act, role: 'support', isYou: false }); return; }
      if (act.name && act.name !== artistName) {
        allActs.push({ name: act.name, role: act.role || 'support', isYou: false, bit_id: act.bit_id || null });
      }
    });
  }

  const artists: BitResolvedArtist[] = [];
  const lookups: Array<Promise<void>> = [];

  // Venue/festival lookup, prefer the user's saved bitVenuePick snapshot.
  const pickId = pick && pick.id;
  const pickIsFestival = !!(pick && pick.isFestival);
  if (isFest && pickId && pickIsFestival) {
    venue = {
      name: String(pick!.name || ''), bit_id: pickId as string | number,
      city: String(pick!.city || ''), region: String(pick!.region || ''), country: String(pick!.country || ''),
      latitude: (pick!.latitude as number) || null, longitude: (pick!.longitude as number) || null,
      isFestival: true, fromAddress: false,
    };
  } else if (!isFest && pickId && !pickIsFestival) {
    const addr = String(pick!.address || '');
    venue = {
      name: String(pick!.name || ''), bit_id: pickId as string | number, address: addr,
      city: addr ? addr.split(',')[0].trim() : '',
      region: addr.split(',').length > 1 ? addr.split(',')[1].trim() : '',
      latitude: (pick!.latitude as number) || null, longitude: (pick!.longitude as number) || null,
      verified: !!pick!.verified, trackerCount: (pick!.trackerCount as number) || 0, fromAddress: false,
    };
  } else if (ev.name && ev.name !== 'TBA') {
    const lookupPath = isFest ? '/api/bandsintown/festival-autocomplete' : '/api/bandsintown/venue-autocomplete';
    lookups.push(
      fetch(AUTH_WORKER + lookupPath + '?query=' + encodeURIComponent(ev.name), { headers: authHeaders('GET'), cache: 'no-store' })
        .then((r) => r.json())
        .then((data: { success?: boolean; venues?: BitHit[]; festivals?: BitHit[] }) => {
          const list = isFest ? data && data.festivals : data && data.venues;
          if (data && data.success && list && list.length > 0) {
            const best = list[0];
            if (isFest) {
              venue = { name: String(best.name || ''), bit_id: best.id ?? null, city: best.city || '', region: best.region || '', isFestival: true, fromAddress: false };
            } else {
              venue = {
                name: String(best.name || ''), bit_id: best.id ?? null, address: best.address,
                city: best.address ? best.address.split(',')[0] : '',
                region: best.address && best.address.split(',').length > 1 ? best.address.split(',')[1].trim() : '',
                verified: best.verified, trackerCount: best.trackerCount, fromAddress: false,
              };
            }
          }
        })
        .catch(() => { /* keep address fallback */ }),
    );
  }

  // Artist lookups, one per lineup member (verbatim, with /artist fallback).
  allActs.forEach((act) => {
    if (act.bit_id) {
      artists.push({ local_name: act.name, bit_name: act.name, bit_id: act.bit_id, role: act.role, isYou: act.isYou, verified: true });
      return;
    }
    lookups.push(
      fetch(AUTH_WORKER + '/api/bandsintown/artist-autocomplete?query=' + encodeURIComponent(act.name), { headers: authHeaders('GET'), cache: 'no-store' })
        .then((r) => r.json())
        .then((data: { success?: boolean; artists?: Array<{ name?: string; id?: string | number; trackerCount?: number; verified?: boolean }> }) => {
          if (data && data.success && data.artists && data.artists.length > 0) {
            const match = data.artists[0];
            artists.push({ local_name: act.name, bit_name: match.name || act.name, bit_id: match.id ?? null, followers: match.trackerCount || 0, role: act.role, isYou: act.isYou, verified: match.verified || true });
            return undefined;
          }
          // Fall back to the public artist endpoint for image / id.
          return fetch(AUTH_WORKER + '/api/bandsintown/artist?artist=' + encodeURIComponent(act.name), { headers: authHeaders('GET'), cache: 'no-store' })
            .then((r2) => r2.json())
            .then((d2: { success?: boolean; artist?: { name?: string; id?: string | number; imageUrl?: string; trackerCount?: number } }) => {
              if (d2 && d2.success && d2.artist) {
                artists.push({ local_name: act.name, bit_name: d2.artist.name || act.name, bit_id: d2.artist.id ?? null, image: d2.artist.imageUrl, followers: d2.artist.trackerCount || 0, role: act.role, isYou: act.isYou, verified: true });
              } else {
                artists.push({ local_name: act.name, bit_name: act.name, role: act.role, isYou: act.isYou, verified: false });
              }
            });
        })
        .catch(() => { artists.push({ local_name: act.name, bit_name: act.name, role: act.role, isYou: act.isYou, verified: false }); }),
    );
  });

  await Promise.all(lookups);
  return { venue, artists, isFest, artistName };
}

/** Confirmed push, POST the full push-event payload (verbatim confirmBitPush). */
export async function confirmBitPush(ev: RawBookingEvent, resolution: BitResolution): Promise<BitPushResult> {
  const { venue: v, artists, isFest, artistName } = resolution;
  const venueName = v ? v.name : ev.name || 'TBA';
  let eventTime = '8:00 PM';
  if (ev.dayOfShow) {
    const raw = ev.dayOfShow.doors || (ev.dayOfShow as { setTime?: string }).setTime || '';
    if (raw) eventTime = String(raw);
  }
  const lineup = artists.map((a) => ({ name: a.bit_name || a.local_name, bit_id: a.bit_id || null, role: a.role }));
  const payloadVenueId = ev.bitVenueId || ((v && v.bit_id && !v.isFestival) ? v.bit_id : null);
  const payloadFestivalId = ev.bitFestivalId || ((v && v.bit_id && v.isFestival) ? v.bit_id : null);

  const payload: Record<string, unknown> = {
    artist_name: artistName,
    date: ev.date,
    time: eventTime,
    venue_name: venueName,
    venue_address: ev.venueAddress || '',
    venue_id: payloadVenueId,
    venue_latitude: (v && v.latitude) || null,
    venue_longitude: (v && v.longitude) || null,
    venue_city: (v && v.city) || '',
    venue_region: (v && v.region) || '',
    festival_id: payloadFestivalId,
    edition_id: isFest ? (ev.bitEditionId || null) : null,
    edition_year: isFest ? (ev.bitEditionYear || null) : null,
    festival_name: isFest ? (ev.bitFestivalName || ev.name || null) : null,
    lineup,
    ticket_url: ev.ticketLink || '',
    title: (ev as { eventTitle?: string }).eventTitle || '',
    description: ev.notes || '',
    event_type: isFest ? 'festival' : 'concert',
    draft: BIT_PUSH_AS_DRAFT,
  };

  try {
    const r = await fetch(AUTH_WORKER + '/api/bandsintown/push-event', {
      method: 'POST',
      headers: authHeaders('POST'),
      body: JSON.stringify(payload),
    });
    const d = await r.json() as { success?: boolean; needsAuth?: boolean; message?: string; error?: string; bitStatus?: number; details?: { raw?: string; message?: string; error?: { message?: string }; errors?: unknown }; debug_info?: { venueName?: string }; bandsintown_url?: string; event?: { payload?: { id?: string } } };
    if (d && d.needsAuth) {
      let msg = d.message || 'BIT dashboard not connected.';
      if (msg.indexOf('Incorrect') > -1) msg = 'Wrong BIT password, update in Settings.';
      return { ok: false, message: msg };
    }
    if (!r.ok || !(d && d.success)) {
      if (d && (d.error === 'venue_not_found' || d.error === 'festival_not_found')) {
        const locType = d.error === 'festival_not_found' ? 'Festival' : 'Venue';
        return { ok: false, message: locType + ' not found in BIT for "' + ((d.debug_info && d.debug_info.venueName) || venueName) + '"' };
      }
      if (d && d.error === 'create_failed') {
        const detail = d.bitStatus ? ' (HTTP ' + d.bitStatus + ')' : '';
        let bitMsg = '';
        if (d.details) {
          const dd = d.details;
          bitMsg = dd.raw || (dd.error && dd.error.message) || (dd.errors ? JSON.stringify(dd.errors) : '') || dd.message || JSON.stringify(dd).substring(0, 200);
        }
        return { ok: false, message: 'BIT rejected' + detail + ': ' + (bitMsg || 'unknown error') };
      }
      return { ok: false, message: 'Push failed: ' + ((d && (d.message || d.error)) || 'Unknown error') };
    }
    return { ok: true, message: 'Pushed to Bandsintown ✓', bitUrl: d.bandsintown_url, bitEventId: d.event?.payload?.id };
  } catch {
    return { ok: false, message: 'Network error, try again.' };
  }
}

/** Short BIT date label (verbatim formatBitDate). */
export function formatBitDate(iso: string | undefined): string {
  if (!iso) return '';
  try {
    const d = new Date(iso + 'T12:00:00');
    return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
  } catch { return String(iso); }
}

// ── Role gates ───────────────────────────────────────────────────────────────
// The MONEY-allowed group (a.k.a. the "Booking" group): Artist owner, Band Member
// (AV), Manager/Management, Booking agent, and Tour Manager. These see the full
// Booking tab + all money surfaces (settlement, lifetime financials, Marketing
// spend, Merch revenue). Everyone else, Label, generic Band Member, FOH/
// Production, Content, Merch, Crew, sees the read-only Calendar and NO money.
const BOOKING_SEATS = new Set([
  'band_member_av',
  'artist',
  'tour_manager',
  'manager',
  'management',
  'booking',
  'booking_agent',
]);

/** Booking/money group vs Calendar (no-money) group, see BOOKING_SEATS comment
 *  for the exact split. Fails CLOSED: unknown seats get Calendar (no money). */
export function canSeeBooking(user: User | null): boolean {
  if (!user) return false;
  if (user.is_band_primary) return true;
  const r = String(user.role || '').toLowerCase();
  if (r === 'artist' || r === 'jedi' || r === 'admin') return true;
  return BOOKING_SEATS.has(String(user.membership_role || '').toLowerCase());
}

/** Alias for clarity at money-surface call sites (Marketing, Merch, settlement).
 *  The money group === the Booking group. */
export const canSeeMoney = canSeeBooking;

/** Lifetime financials, the whole money group gets the FULL view (Tour Manager
 *  now included, per the access rule). */
export function showLifetime(user: User | null): boolean {
  return canSeeBooking(user);
}

// ── Hook ─────────────────────────────────────────────────────────────────────
export interface BookingData {
  readonly user: User | null;
  readonly stats: BookingStats;
  readonly shows: ReadonlyArray<BookingShow>;
  readonly quotes: ReadonlyArray<BookingQuote>;
  readonly loading: boolean;
  /** Re-fetch the schedule + quotes (call after a save/delete). */
  readonly reload: () => void;
}

const EMPTY_STATS: BookingStats = {
  count: 0,
  guaranteesYtd: 0,
  guaranteesLife: 0,
  merchYtd: 0,
  merchLife: 0,
  ticketsYtd: 0,
  ticketsLife: 0,
  netYtd: 0,
  netLife: 0,
};

interface BookingCore {
  readonly user: User | null;
  readonly stats: BookingStats;
  readonly shows: ReadonlyArray<BookingShow>;
  readonly quotes: ReadonlyArray<BookingQuote>;
}

export function useBookingData(): BookingData {
  const { data, loading, reload } = useCachedData<BookingCore>('booking', async () => {
    const [user, schedRes, quotes, sharedCollab] = await Promise.all([
      fetchCurrentUser().catch(() => null),
      apiGet<ScheduleResponse>('auth', '/api/data/schedule').catch(() => null),
      fetchQuotes().catch(() => [] as BookingQuote[]),
      fetchAllSharedCollab().catch(() => ({} as Record<string, { basics: Partial<RawBookingEvent>; lineup: LineupAct[]; dayOfShow: Record<string, unknown>; updatedAt: string | null }>)),
    ]);
    /* Merge connected shows' shared BASICS in at list level so tour name (→ tour
       folders), venue and capacity reflect the shared source of truth without
       opening each show. Lineup/RoS are left to the show modal's live merge, so a
       stale share snapshot can't clobber the list's lineup. (Ryan 2026-07-22) */
    const events = unwrapArr<RawBookingEvent>(schedRes).filter((e) => e && e.date && !isHiddenForMe(e))
      .map((e) => {
        const c = sharedCollab[String(e.date).slice(0, 10)];
        return c ? mergeSharedCollab(e, { basics: c.basics }) : e;
      });
    const shows = events.map(toShow).sort((a, b) => a.date.localeCompare(b.date));
    return { user, stats: calcStats(events), shows, quotes };
  });
  const core = data ?? { user: null, stats: EMPTY_STATS, shows: [], quotes: [] };
  return { ...core, loading, reload };
}
